(() => {
  const manifestSelector = '[data-bundle-drawer-manifest]';
  const screenSelector = '[data-screen-type]';
  const nextSelector = '.plan-drawer__cta, .pud-partner__cta, .plan-drawer__mobile-cta-paid, .plan-drawer__skip';
  const backSelector = '.plan-drawer__back, .pud-partner__back';
  const closeSelector = '[data-bundle-drawer-close], .plan-drawer__close, .pud-partner__close';
  const editSelector = '[data-summary-target]';

  function parseJson(text) {
    if (!text) {
      return null;
    }

    try {
      return JSON.parse(text);
    } catch (error) {
      const decoded = String(text)
        .replace(/&quot;/g, '"')
        .replace(/&#34;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&');

      try {
        return JSON.parse(decoded);
      } catch (decodedError) {
        return null;
      }
    }
  }

  function normalizeManifest(value) {
    if (!value) {
      return { screenOrder: [] };
    }

    if (typeof value === 'string') {
      return normalizeManifest(parseJson(value));
    }

    if (typeof value === 'object') {
      return value;
    }

    return { screenOrder: [] };
  }

  function readManifest(root) {
    const manifestNode = root.querySelector(manifestSelector);

    if (!manifestNode) {
      return { screenOrder: [] };
    }

    return normalizeManifest(parseJson(manifestNode.textContent.trim()));
  }

  function canonicalScreenType(type) {
    if (type === null || type === undefined) {
      return '';
    }

    const normalized = String(type)
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');

    switch (normalized) {
      case 'variant':
      case 'variant_selector':
      case 'watch_variant':
      case 'watch_variants':
      case 'smartwatch':
        return 'variant_selector';
      case 'watch_plan':
      case 'plan':
      case 'plan_selector':
        return 'watch_plan';
      case 'strap':
      case 'strap_selector':
      case 'watch_strap':
      case 'watch_straps':
        return 'strap_selector';
      case 'accessory':
      case 'accessories':
      case 'accessory_selector':
        return 'accessory_selector';
      case 'summary':
      case 'order_summary':
        return 'order_summary';
      case 'mobile':
      case 'mobile_plan':
      case 'mobile_upsell':
        return 'mobile_plan';
      default:
        return normalized;
    }
  }

  function targetIdToType(targetId) {
    switch (String(targetId || '').replace(/^#/, '')) {
      case 'bundle-watch-plan':
        return 'watch_plan';
      case 'bundle-watch-straps':
        return 'strap_selector';
      case 'bundle-accessories':
        return 'accessory_selector';
      case 'bundle-order-summary':
        return 'order_summary';
      case 'bundle-mobile-plan':
        return 'mobile_plan';
      case 'bundle-variant-selector':
      case 'bundle-smartwatch':
        return 'variant_selector';
      default:
        return '';
    }
  }

  function mountFlow(entry, stageRoot) {
    const manifest = readManifest(entry);
    const stage = (stageRoot || entry).querySelector('[data-bundle-drawer-stage]');

    if (!stage) {
      return null;
    }

    const screens = Array.from(stage.querySelectorAll(screenSelector));
    const screenMap = new Map(
      screens.map((screen) => [canonicalScreenType(screen.getAttribute('data-screen-type')), screen])
    );

    const orderedScreensCandidate = [manifest?.screenOrder, manifest?.screens, manifest?.screen_order, manifest?.steps]
      .find((value) => Array.isArray(value) || typeof value === 'string');

    let orderedScreensRaw = [];
    if (Array.isArray(orderedScreensCandidate)) {
      orderedScreensRaw = orderedScreensCandidate;
    } else if (typeof orderedScreensCandidate === 'string') {
      const parsed = parseJson(orderedScreensCandidate);
      if (Array.isArray(parsed)) {
        orderedScreensRaw = parsed;
      }
    }

    const orderedTypes = orderedScreensRaw
      .map((screen) => {
        if (typeof screen === 'string') {
          return canonicalScreenType(screen);
        }

        if (screen && typeof screen === 'object') {
          const rawType = screen.type || screen.id || screen.screen || screen.key || screen.screenType || screen.name;
          return canonicalScreenType(rawType);
        }

        return '';
      })
      .filter((type) => screenMap.has(type));

    let finalTypes = orderedTypes.length
      ? orderedTypes
      : screens.map((screen) => canonicalScreenType(screen.getAttribute('data-screen-type')));

    const finalScreens = finalTypes.map((type) => screenMap.get(type)).filter(Boolean);

    finalScreens.forEach((screen) => stage.appendChild(screen));

    let activeIndex = 0;

    const showScreen = (index) => {
      activeIndex = Math.max(0, Math.min(index, finalScreens.length - 1));

      finalScreens.forEach((screen, screenIndex) => {
        const isActive = screenIndex === activeIndex;
        screen.classList.toggle('is-active', isActive);
        screen.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        const isFirstScreen = screenIndex === 0;
        const backButtons = screen.querySelectorAll(backSelector);
        backButtons.forEach((button) => {
          button.disabled = isFirstScreen;
          button.setAttribute('aria-disabled', isFirstScreen ? 'true' : 'false');
          button.style.visibility = isFirstScreen ? 'hidden' : '';
          button.style.pointerEvents = isFirstScreen ? 'none' : '';
        });
      });

      stage.scrollTop = 0;
      const dialog = entry.querySelector('[data-bundle-drawer-dialog]');

      if (dialog) {
        dialog.scrollTop = 0;
      }
    };

    showScreen(0);

    return {
      next() {
        showScreen(activeIndex + 1);
      },
      prev() {
        showScreen(activeIndex - 1);
      },
      goTo(type) {
        const targetIndex = finalTypes.indexOf(canonicalScreenType(type));

        if (targetIndex >= 0) {
          showScreen(targetIndex);
        }
      },
      reset() {
        showScreen(0);
      },
      hasType(type) {
        return finalTypes.includes(canonicalScreenType(type));
      },
      activeType() {
        return finalTypes[activeIndex] || '';
      },
      nextType() {
        const nextIndex = Math.max(0, Math.min(activeIndex + 1, finalTypes.length - 1));
        return finalTypes[nextIndex] || '';
      },
      orderedTypes() {
        return finalTypes.slice();
      },
      isLast() {
        return activeIndex >= finalTypes.length - 1;
      }
    };
  }

  function toNonNegativeInteger(value) {
    const number = Number(value);
    if (!Number.isFinite(number) || number < 0) {
      return null;
    }

    return Math.floor(number);
  }

  function parseNonNegativePercent(value) {
    const direct = toNonNegativeInteger(value);
    if (direct !== null) {
      return direct;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const match = value.match(/\d+(?:\.\d+)?/);
    if (!match) {
      return null;
    }

    const parsed = Number(match[0]);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return null;
    }

    return Math.floor(parsed);
  }

  function readAllowanceField(source) {
    if (!source || typeof source !== 'object') {
      return null;
    }

    const direct = toNonNegativeInteger(source.freeAllowance);
    if (direct !== null) {
      return direct;
    }

    const snake = toNonNegativeInteger(source.free_allowance);
    if (snake !== null) {
      return snake;
    }

    const qty = toNonNegativeInteger(source.freeQty);
    if (qty !== null) {
      return qty;
    }

    const qtySnake = toNonNegativeInteger(source.free_qty);
    if (qtySnake !== null) {
      return qtySnake;
    }

    return toNonNegativeInteger(source.allowance);
  }

  function resolveStrapFreeAllowance(manifest) {
    const groups = [];

    if (Array.isArray(manifest?.screenOrder)) {
      groups.push(manifest.screenOrder);
    }

    if (Array.isArray(manifest?.screens)) {
      groups.push(manifest.screens);
    }

    for (const group of groups) {
      for (const screen of group) {
        if (!screen || typeof screen !== 'object') {
          continue;
        }

        const rawType = screen.type || screen.id || screen.screen || screen.key;
        if (canonicalScreenType(rawType) !== 'strap_selector') {
          continue;
        }

        const value = readAllowanceField(screen);
        if (value !== null) {
          return value;
        }
      }
    }

    const ruleCandidates = [
      manifest?.rules?.strap_free_qty,
      manifest?.rules?.strapFreeQty,
      manifest?.strap_free_qty,
      manifest?.strapFreeQty,
      manifest?.freeAllowance,
      manifest?.free_allowance
    ];

    for (const candidate of ruleCandidates) {
      const value = toNonNegativeInteger(candidate);
      if (value !== null) {
        return value;
      }
    }

    return null;
  }

  function resolveMobilePlanPercentageOff(manifest) {
    const mobileScreen = findScreenConfig(manifest, 'mobile_plan');
    if (mobileScreen) {
      const candidateFields = [
        mobileScreen.percentageOff,
        mobileScreen.percentage_off,
        mobileScreen.percentOff,
        mobileScreen.percent_off,
        mobileScreen.offPercentage,
        mobileScreen.off_percentage,
        mobileScreen.discountPercent,
        mobileScreen.discount_percent,
        mobileScreen.discountPercentage,
        mobileScreen.discount_percentage,
        mobileScreen.discount?.percentageOff,
        mobileScreen.discount?.percentage_off
      ];

      for (const candidate of candidateFields) {
        const value = parseNonNegativePercent(candidate);
        if (value !== null) {
          return value;
        }
      }
    }

    const manifestLevelCandidates = [
      manifest?.percentageOff,
      manifest?.percentage_off,
      manifest?.percentOff,
      manifest?.percent_off,
      manifest?.offPercentage,
      manifest?.off_percentage,
      manifest?.discountPercent,
      manifest?.discount_percent,
      manifest?.discountPercentage,
      manifest?.discount_percentage,
      manifest?.rules?.mobile_percentage_off,
      manifest?.rules?.mobilePercentageOff,
      manifest?.rules?.mobile_plan_percentage_off,
      manifest?.rules?.mobilePlanPercentageOff
    ];

    for (const candidate of manifestLevelCandidates) {
      const value = parseNonNegativePercent(candidate);
      if (value !== null) {
        return value;
      }
    }

    return null;
  }

  function resolveMobilePlanDiscountDuration(manifest) {
    const mobileScreen = findScreenConfig(manifest, 'mobile_plan');
    if (mobileScreen) {
      const candidateFields = [
        mobileScreen.duration,
        mobileScreen.discountDuration,
        mobileScreen.discount_duration,
        mobileScreen.durationMonths,
        mobileScreen.duration_months,
        mobileScreen.months,
        mobileScreen.discount?.duration,
        mobileScreen.discount?.durationMonths,
        mobileScreen.discount?.duration_months
      ];

      for (const candidate of candidateFields) {
        const value = toNonNegativeInteger(candidate);
        if (value !== null) {
          return Math.max(1, value);
        }
      }
    }

    const manifestLevelCandidates = [
      manifest?.duration,
      manifest?.discountDuration,
      manifest?.discount_duration,
      manifest?.durationMonths,
      manifest?.duration_months,
      manifest?.months,
      manifest?.rules?.mobile_discount_duration,
      manifest?.rules?.mobileDiscountDuration,
      manifest?.rules?.mobile_duration_months,
      manifest?.rules?.mobileDurationMonths,
      manifest?.rules?.mobile_months,
      manifest?.rules?.mobileMonths
    ];

    for (const candidate of manifestLevelCandidates) {
      const value = toNonNegativeInteger(candidate);
      if (value !== null) {
        return Math.max(1, value);
      }
    }

    return 1;
  }

  function getManifestScreenGroups(manifest) {
    const groups = [];

    const candidates = [
      manifest?.screenOrder,
      manifest?.screens,
      manifest?.screen_order,
      manifest?.steps
    ];

    candidates.forEach((candidate) => {
      if (Array.isArray(candidate)) {
        groups.push(candidate);
        return;
      }

      if (typeof candidate === 'string') {
        const parsed = parseJson(candidate);
        if (Array.isArray(parsed)) {
          groups.push(parsed);
        }
      }
    });

    return groups;
  }

  function findScreenConfig(manifest, targetType) {
    const normalizedTarget = canonicalScreenType(targetType);
    const groups = getManifestScreenGroups(manifest);

    for (const group of groups) {
      for (const screen of group) {
        if (!screen || typeof screen !== 'object') {
          continue;
        }

        const rawType = screen.type || screen.id || screen.screen || screen.key || screen.screenType || screen.name;
        if (canonicalScreenType(rawType) === normalizedTarget) {
          return screen;
        }
      }
    }

    return null;
  }

  function normalizeBundleDisplayMode(value) {
    if (typeof value !== 'string') {
      return null;
    }

    const normalized = value.trim().toLowerCase().replace(/[\s_]+/g, '-');

    if (normalized === 'subtotal-only' || normalized === 'subtotal') {
      return 'subtotal-only';
    }

    if (normalized === 'default' || normalized === 'line-items' || normalized === 'line-item' || normalized === 'full') {
      return 'default';
    }

    if (normalized === 'bundle' || normalized === 'builder' || normalized === 'selection-only') {
      return 'bundle';
    }

    return null;
  }

  function resolveBundleDisplayMode(manifest) {
    const groups = [];

    if (Array.isArray(manifest?.screenOrder)) {
      groups.push(manifest.screenOrder);
    }

    if (Array.isArray(manifest?.screens)) {
      groups.push(manifest.screens);
    }

    for (const group of groups) {
      for (const screen of group) {
        if (!screen || typeof screen !== 'object') {
          continue;
        }

        const candidateFields = [
          screen.bundleDisplayMode,
          screen.bundle_display_mode,
          screen.displayMode,
          screen.display_mode,
          screen.bundleMode,
          screen.bundle_mode
        ];

        for (const candidate of candidateFields) {
          const mode = normalizeBundleDisplayMode(candidate);
          if (mode) {
            return mode;
          }
        }
      }
    }

    const manifestLevelCandidates = [
      manifest?.bundleDisplayMode,
      manifest?.bundle_display_mode,
      manifest?.displayMode,
      manifest?.display_mode,
      manifest?.bundleMode,
      manifest?.bundle_mode,
      manifest?.rules?.bundleDisplayMode,
      manifest?.rules?.bundle_display_mode,
      manifest?.rules?.displayMode,
      manifest?.rules?.display_mode
    ];

    for (const candidate of manifestLevelCandidates) {
      const mode = normalizeBundleDisplayMode(candidate);
      if (mode) {
        return mode;
      }
    }

    return 'default';
  }

  function resolveFlowMode(manifest) {
    const candidates = [
      manifest?.flowMode,
      manifest?.flow_mode,
      manifest?.mode,
      manifest?.rules?.flowMode,
      manifest?.rules?.flow_mode,
      manifest?.rules?.mode
    ];

    for (const candidate of candidates) {
      if (typeof candidate !== 'string') {
        continue;
      }

      const normalized = candidate.trim().toLowerCase().replace(/[\s_]+/g, '-');
      if (normalized === 'upsell' || normalized === 'upsell-only' || normalized === 'offer') {
        return 'upsell';
      }

      if (normalized === 'bundle' || normalized === 'bundle-builder' || normalized === 'builder') {
        return 'bundle';
      }
    }

    return 'bundle';
  }

  function resolveUpsellCartGrouping(manifest) {
    const candidates = [
      manifest?.upsellGroupInCart,
      manifest?.upsell_group_in_cart,
      manifest?.upsellBundleInCart,
      manifest?.upsell_bundle_in_cart,
      manifest?.rules?.upsellGroupInCart,
      manifest?.rules?.upsell_group_in_cart,
      manifest?.rules?.upsellBundleInCart,
      manifest?.rules?.upsell_bundle_in_cart
    ];

    for (const candidate of candidates) {
      if (candidate === true || candidate === false) {
        return candidate;
      }

      if (typeof candidate === 'string') {
        const normalized = candidate.trim().toLowerCase();
        if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
          return true;
        }

        if (normalized === 'false' || normalized === '0' || normalized === 'no') {
          return false;
        }
      }
    }

    // Default to grouped in cart for upsell safety (hide/remove controls per bundle logic).
    return true;
  }

  function normalizePropertyKey(value) {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim();
  }

  function normalizePropertyValue(value) {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value);
  }

  function applyDiscountTokens(value, percentageOff) {
    const text = normalizePropertyValue(value);
    if (!text) {
      return text;
    }

    return text
      .replace(/\{\{\s*percentageOff\s*\}\}/gi, String(percentageOff))
      .replace(/\{\{\s*precentageOff\s*\}\}/gi, String(percentageOff))
      .replace(/\{\s*percentageOff\s*\}/gi, String(percentageOff))
      .replace(/\{\s*precentageOff\s*\}/gi, String(percentageOff))
      .replace(/\$\{\s*percentageOff\s*\}/gi, String(percentageOff));
  }

  function extractKeyValueProperties(source, percentageOff) {
    const output = {};
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      return output;
    }

    Object.keys(source).forEach((rawKey) => {
      const key = applyDiscountTokens(normalizePropertyKey(rawKey), percentageOff);
      if (!key) {
        return;
      }

      output[key] = applyDiscountTokens(source[rawKey], percentageOff);
    });

    return output;
  }

  function extractKeyArrayProperties(source, percentageOff) {
    const output = {};
    if (!Array.isArray(source)) {
      return output;
    }

    source.forEach((rawKey) => {
      const key = normalizePropertyKey(rawKey);
      if (!key) {
        return;
      }

      output[applyDiscountTokens(key, percentageOff)] = '1';
    });

    return output;
  }

  function resolveRegiosProperties(manifest, percentageOff) {
    const mobileScreen = findScreenConfig(manifest, 'mobile_plan');
    const mobileDiscountDuration = resolveMobilePlanDiscountDuration(manifest);

    function formatMobilePromoLabel(percent, durationMonths) {
      const normalizedPercent = Math.max(0, Math.min(100, Number(percent) || 0));
      void durationMonths;

      if (normalizedPercent >= 100) {
        return 'Free Mobile Plan';
      }

      return `${normalizedPercent}% off Mobile Plan`;
    }

    const simpleMobileDiscountKey = normalizePropertyKey(
      mobileScreen?.discount_key
      || mobileScreen?.discountKey
      || mobileScreen?.regios_discount_key
      || manifest?.discount_key
      || manifest?.discountKey
      || manifest?.rules?.discount_key
      || manifest?.rules?.discountKey
    );

    const simpleMobileDiscountValueRaw =
      mobileScreen?.discount_value
      ?? mobileScreen?.discountValue
      ?? mobileScreen?.regios_discount_value
      ?? manifest?.discount_value
      ?? manifest?.discountValue
      ?? manifest?.rules?.discount_value
      ?? manifest?.rules?.discountValue;

    const simpleMobileDiscountValue = normalizePropertyValue(
      simpleMobileDiscountValueRaw === '' || simpleMobileDiscountValueRaw === null || simpleMobileDiscountValueRaw === undefined
        ? '1'
        : simpleMobileDiscountValueRaw
    );

    const mobilePropertyCandidates = [
      mobileScreen?.regios?.mobile?.properties,
      mobileScreen?.regios?.properties,
      mobileScreen?.regiosProperties,
      mobileScreen?.regios_properties,
      mobileScreen?.offerProperties,
      mobileScreen?.offer_properties,
      manifest?.regiosProperties,
      manifest?.regios_properties,
      manifest?.rules?.regiosProperties,
      manifest?.rules?.regios_properties
    ];

    const mobileKeyCandidates = [
      mobileScreen?.regios?.mobile?.keys,
      mobileScreen?.regios?.keys,
      mobileScreen?.regiosKeys,
      mobileScreen?.regios_keys,
      mobileScreen?.offerKeys,
      mobileScreen?.offer_keys,
      manifest?.regiosKeys,
      manifest?.regios_keys,
      manifest?.rules?.regiosKeys,
      manifest?.rules?.regios_keys
    ];

    const resolved = {
      mobile: {},
      sim: {}
    };

    if (simpleMobileDiscountKey) {
      resolved.mobile[applyDiscountTokens(simpleMobileDiscountKey, percentageOff)] = applyDiscountTokens(simpleMobileDiscountValue, percentageOff);
    }

    mobilePropertyCandidates.forEach((candidate) => {
      Object.assign(resolved.mobile, extractKeyValueProperties(candidate, percentageOff));
    });

    mobileKeyCandidates.forEach((candidate) => {
      Object.assign(resolved.mobile, extractKeyArrayProperties(candidate, percentageOff));
    });

    if (Object.keys(resolved.mobile).length > 0 || percentageOff !== null) {
      resolved.mobile = {
        Promo: formatMobilePromoLabel(percentageOff, mobileDiscountDuration)
      };
    }

    resolved.sim = {};

    return resolved;
  }

  function resolveScreenDiscountProperties(manifest, screenType) {
    const screen = findScreenConfig(manifest, screenType);
    if (!screen || typeof screen !== 'object') {
      return {};
    }

    const screenPercentageOffCandidates = [
      screen?.percentageOff,
      screen?.percentage_off,
      screen?.percentOff,
      screen?.percent_off,
      screen?.offPercentage,
      screen?.off_percentage,
      screen?.discountPercent,
      screen?.discount_percent,
      screen?.discountPercentage,
      screen?.discount_percentage
    ];

    let percentageOff = null;
    for (const candidate of screenPercentageOffCandidates) {
      const value = parseNonNegativePercent(candidate);
      if (value !== null) {
        percentageOff = value;
        break;
      }
    }

    const simpleDiscountKey = normalizePropertyKey(
      screen?.discount_key
      || screen?.discountKey
      || screen?.regios_discount_key
    );

    const simpleDiscountValueRaw =
      screen?.discount_value
      ?? screen?.discountValue
      ?? screen?.regios_discount_value;

    const simpleDiscountValue = normalizePropertyValue(
      simpleDiscountValueRaw === '' || simpleDiscountValueRaw === null || simpleDiscountValueRaw === undefined
        ? '1'
        : simpleDiscountValueRaw
    );

    const resolved = {};

    if (simpleDiscountKey) {
      resolved[applyDiscountTokens(simpleDiscountKey, percentageOff)] = applyDiscountTokens(simpleDiscountValue, percentageOff);
    }

    Object.assign(
      resolved,
      extractKeyValueProperties(screen?.discountProperties, percentageOff),
      extractKeyValueProperties(screen?.discount_properties, percentageOff),
      extractKeyValueProperties(screen?.regiosProperties, percentageOff),
      extractKeyValueProperties(screen?.regios_properties, percentageOff)
    );

    Object.assign(
      resolved,
      extractKeyArrayProperties(screen?.discountKeys, percentageOff),
      extractKeyArrayProperties(screen?.discount_keys, percentageOff),
      extractKeyArrayProperties(screen?.regiosKeys, percentageOff),
      extractKeyArrayProperties(screen?.regios_keys, percentageOff)
    );

    if (Object.keys(resolved).length === 0) {
      if (screenType === 'strap_selector') {
        const screenFreeAllowance = readAllowanceField(screen);
        if (screenFreeAllowance !== null && screenFreeAllowance > 0) {
          resolved.Promo = 'Free Strap';
        } else if (percentageOff !== null) {
          resolved.Promo = `${percentageOff}% off Strap`;
        }
      } else if (screenType === 'accessory_selector') {
        if (percentageOff !== null) {
          resolved.Promo = `${percentageOff}% off Accessory`;
        }
      }
    }

    return resolved;
  }

  function resolveIncludedAccessoryDiscountProperties(manifest) {
    const screen = findScreenConfig(manifest, 'accessory_selector');
    if (!screen || typeof screen !== 'object') {
      return { Promo: 'Free Accessory' };
    }

    const screenPercentageOffCandidates = [
      screen?.included_percentage_off,
      screen?.includedPercentageOff,
      screen?.included_discount_percent,
      screen?.includedDiscountPercent,
      screen?.included_discount_percentage,
      screen?.includedDiscountPercentage,
      screen?.percentageOff,
      screen?.percentage_off
    ];

    let percentageOff = 100;
    for (const candidate of screenPercentageOffCandidates) {
      const value = parseNonNegativePercent(candidate);
      if (value !== null) {
        percentageOff = value;
        break;
      }
    }

    const simpleDiscountKey = normalizePropertyKey(
      screen?.included_discount_key
      || screen?.includedDiscountKey
      || screen?.included_regios_discount_key
      || screen?.includedRegiosDiscountKey
    );

    const simpleDiscountValueRaw =
      screen?.included_discount_value
      ?? screen?.includedDiscountValue
      ?? screen?.included_regios_discount_value
      ?? screen?.includedRegiosDiscountValue;

    const simpleDiscountValue = normalizePropertyValue(
      simpleDiscountValueRaw === '' || simpleDiscountValueRaw === null || simpleDiscountValueRaw === undefined
        ? '1'
        : simpleDiscountValueRaw
    );

    const resolved = {};

    if (simpleDiscountKey) {
      resolved[applyDiscountTokens(simpleDiscountKey, percentageOff)] = applyDiscountTokens(simpleDiscountValue, percentageOff);
    }

    Object.assign(
      resolved,
      extractKeyValueProperties(screen?.includedDiscountProperties, percentageOff),
      extractKeyValueProperties(screen?.included_discount_properties, percentageOff),
      extractKeyValueProperties(screen?.includedRegiosProperties, percentageOff),
      extractKeyValueProperties(screen?.included_regios_properties, percentageOff)
    );

    Object.assign(
      resolved,
      extractKeyArrayProperties(screen?.includedDiscountKeys, percentageOff),
      extractKeyArrayProperties(screen?.included_discount_keys, percentageOff),
      extractKeyArrayProperties(screen?.includedRegiosKeys, percentageOff),
      extractKeyArrayProperties(screen?.included_regios_keys, percentageOff)
    );

    if (Object.keys(resolved).length === 0) {
      resolved.Promo = 'Free Accessory';
    }

    return resolved;
  }

  function manifestIncludesScreenType(manifest, targetType) {
    const target = canonicalScreenType(targetType);
    const groups = [];
    const possibleGroups = [
      manifest?.screenOrder,
      manifest?.screens,
      manifest?.screen_order,
      manifest?.steps
    ];

    possibleGroups.forEach((group) => {
      if (Array.isArray(group)) {
        groups.push(group);
        return;
      }

      if (typeof group === 'string') {
        const parsed = parseJson(group);
        if (Array.isArray(parsed)) {
          groups.push(parsed);
        }
      }
    });

    if (groups.length === 0 && Array.isArray(manifest?.screenOrder) === false && Array.isArray(manifest?.screens) === false) {
      const single = manifest?.type || manifest?.id || manifest?.screen || manifest?.key || manifest?.screenType || manifest?.name;
      if (single && canonicalScreenType(single) === target) {
        return true;
      }
    }

    return groups.some((group) => group.some((screen) => {
      if (typeof screen === 'string') {
        return canonicalScreenType(screen) === target;
      }

      if (screen && typeof screen === 'object') {
        const rawType = screen.type || screen.id || screen.screen || screen.key || screen.screenType || screen.name;
        return canonicalScreenType(rawType) === target;
      }

      return false;
    }));
  }

  function boot() {
    document.querySelectorAll('[data-bundle-drawer-entry]').forEach((entry) => {
      let isSubmitting = false;
      let submittingShownAt = 0;

      const manifest = readManifest(entry);
      const trigger = entry.querySelector('[data-bundle-drawer-trigger]');
      const overlay = entry.querySelector('[data-bundle-drawer-overlay]');
      const quantityInput = entry.querySelector('[data-bundle-drawer-quantity]');
      const triggerPriceNode = entry.querySelector('[data-bundle-trigger-price]');
      const triggerDefaultLabel = trigger?.getAttribute('data-bundle-trigger-default-label') || 'Add to Cart';
      const triggerUnitPriceCents = Number(trigger?.getAttribute('data-bundle-trigger-price-cents') || '0');
      const currencyCode = entry.getAttribute('data-bundle-drawer-currency') || 'AUD';
      const moneyFormatFromEntry = entry.getAttribute('data-bundle-money-format') || '';
      const moneyWithCurrencyFormatFromEntry = entry.getAttribute('data-bundle-money-with-currency-format') || '';
      const productFormId = entry.getAttribute('data-bundle-product-form-id') || '';
      const productInfo = entry.closest('product-info');
      let latestPdpVariant = null;

      // Move the overlay out of the PDP container so fixed positioning is viewport-based.
      if (overlay && overlay.parentNode !== document.body) {
        document.body.appendChild(overlay);
      }

      const flow = mountFlow(entry, overlay);

      if (!trigger || !overlay || !flow) {
        return;
      }

      const resolveShopifyMoneyFormat = () => {
        if (window.theme?.settings?.currencyCodeEnabled) {
          return window.theme?.settings?.moneyWithCurrencyFormat || moneyWithCurrencyFormatFromEntry || window.theme?.settings?.moneyFormat || moneyFormatFromEntry || '${{amount}}';
        }

        return window.theme?.settings?.moneyFormat || moneyFormatFromEntry || '${{amount}}';
      };

      const formatMoneyWithToken = (amountCents, format) => {
        const tokenMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
        const token = tokenMatch ? tokenMatch[1] : 'amount';

        const numberWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
          const amount = Number(number);
          if (!Number.isFinite(amount)) {
            return '0';
          }

          const fixed = (amount / 100).toFixed(precision);
          const parts = fixed.split('.');
          const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          const cents = parts[1] ? decimal + parts[1] : '';
          return dollars + cents;
        };

        let value = '';
        switch (token) {
          case 'amount':
            value = numberWithDelimiters(amountCents, 2, ',', '.');
            break;
          case 'amount_no_decimals':
            value = numberWithDelimiters(amountCents, 0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            value = numberWithDelimiters(amountCents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = numberWithDelimiters(amountCents, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = numberWithDelimiters(amountCents, 2, "'", '.');
            break;
          default:
            value = numberWithDelimiters(amountCents, 2, ',', '.');
            break;
        }

        return format.replace(/\{\{\s*\w+\s*\}\}/, value);
      };

      const formatMoneyCents = (cents) => {
        const amountCents = Math.max(0, Math.round(Number(cents) || 0));

        if (window.theme?.Currency?.formatMoney) {
          return window.theme.Currency.formatMoney(amountCents, resolveShopifyMoneyFormat());
        }

        try {
          return formatMoneyWithToken(amountCents, resolveShopifyMoneyFormat());
        } catch (error) {
          return formatMoneyWithToken(amountCents, moneyFormatFromEntry || '${{amount}}');
        }
      };

      const getActiveProductForm = () => {
        if (productFormId) {
          const formById = document.getElementById(productFormId);
          if (formById) {
            return formById;
          }
        }

        return (
          document.querySelector('[data-product-form]')
          || document.querySelector('form[action*="/cart/add"]')
          || document.querySelector('product-form')
          || null
        );
      };

      const clampBundleQuantity = (value) => {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) {
          return 1;
        }

        return Math.max(1, Math.min(99, Math.floor(parsed)));
      };

      const resolveSourceQuantity = () => {
        const selectors = [];

        if (productFormId) {
          selectors.push(`input[name="quantity"][form="${CSS.escape(productFormId)}"]`);
          selectors.push(`input[name="quantity"][form='${productFormId}']`);
        }

        selectors.push('[data-product-form] input[name="quantity"]');
        selectors.push('form[action*="/cart/add"] input[name="quantity"]');

        for (const selector of selectors) {
          const node = document.querySelector(selector);
          if (!node) {
            continue;
          }

          return clampBundleQuantity(node.value || node.getAttribute('value') || 1);
        }

        return null;
      };

      const getBundleQuantity = () => clampBundleQuantity(resolveSourceQuantity() ?? quantityInput?.value ?? 1);

      const syncBundleQuantity = (nextValue) => {
        if (!quantityInput) {
          return;
        }

        const quantity = clampBundleQuantity(nextValue);
        quantityInput.value = String(quantity);

        if (triggerPriceNode && triggerUnitPriceCents > 0) {
          triggerPriceNode.textContent = formatMoneyCents(triggerUnitPriceCents * quantity);
        }
      };

      if (quantityInput) {
        const syncFromQuantityInput = () => {
          syncBundleQuantity(quantityInput.value);
        };

        const quantityControl = quantityInput.closest('quantity-input');
        if (quantityControl) {
          quantityControl.addEventListener('click', (event) => {
            const button = event.target.closest('button[name="minus"], button[name="plus"]');
            if (!button) {
              return;
            }

            window.requestAnimationFrame(syncFromQuantityInput);
          });
        }

        quantityInput.addEventListener('input', syncFromQuantityInput);
        quantityInput.addEventListener('change', () => {
          syncBundleQuantity(quantityInput.value);
        });

        quantityInput.addEventListener('blur', () => {
          syncBundleQuantity(quantityInput.value);
        });

        syncBundleQuantity(quantityInput.value);
      }

      const strapScreen = overlay.querySelector('[data-screen-type="strap_selector"]');
      const strapAllowance = strapScreen?.querySelector('[data-free-strap-allowance]');
      const strapCta = strapScreen?.querySelector('[data-free-strap-cta]');
      const strapTitle = strapScreen?.querySelector('[data-free-strap-title]');
      const strapSub = strapScreen?.querySelector('[data-free-strap-sub]');
      const accessoryScreen = overlay.querySelector('[data-screen-type="accessory_selector"]');
      const accessorySummary = accessoryScreen?.querySelector('[data-accessory-addon-summary]');
      const accessoryCta = accessoryScreen?.querySelector('[data-accessory-cta]');
      const accessoryGrid = accessoryScreen?.querySelector('[data-accessory-addon-grid]');
      const includedAccessories = accessoryScreen?.querySelector('[data-included-accessories]');
      const includedAccessoryCards = accessoryScreen ? Array.from(accessoryScreen.querySelectorAll('[data-included-accessory-card]')) : [];
      const accessoryEmpty = accessoryScreen?.querySelector('.g-upsell-empty');
      const summaryScreen = overlay.querySelector('[data-screen-type="order_summary"]');
      const summarySubtotal = summaryScreen?.querySelector('[data-summary-subtotal]');
      const summaryAddons = summaryScreen?.querySelector('[data-summary-addons]');
      const summaryTotal = summaryScreen?.querySelector('[data-summary-total]');
      const summaryRrpBlurb = summaryScreen?.querySelector('[data-summary-rrp-blurb]');
      const summaryMobileOptin = summaryScreen?.querySelector('[data-mobile-optin]');
      const summaryMobileOptinLabel = summaryScreen?.querySelector('[data-mobile-optin-label]');
      const summaryCtaButton = summaryScreen?.querySelector('.pud-partner__cta');
      const summaryMobileOfferTitle = summaryScreen?.querySelector('[data-mobile-offer-summary-title]');
      const summaryMobileOfferDesc = summaryScreen?.querySelector('[data-mobile-offer-summary-desc]');
      const mobilePlanScreen = overlay.querySelector('[data-screen-type="mobile_plan"]');
      const watchPlanScreen = overlay.querySelector('[data-screen-type="watch_plan"]');
      const mobilePlanOfferHeading = mobilePlanScreen?.querySelector('[data-mobile-offer-heading]');
      const mobilePlanName = mobilePlanScreen?.querySelector('[data-mobile-selected-name]');
      const mobilePlanDetail = mobilePlanScreen?.querySelector('[data-mobile-selected-detail]');
      const mobilePlanCtaLabel = mobilePlanScreen?.querySelector('.plan-drawer__mobile-cta-paid .plan-drawer__btn-label');
      const mobilePlanCtaButton = mobilePlanScreen?.querySelector('.plan-drawer__mobile-cta-paid');
      const mobileSkipButton = mobilePlanScreen?.querySelector('.plan-drawer__skip');
      const mobileSkipButtons = mobilePlanScreen
        ? Array.from(mobilePlanScreen.querySelectorAll('.plan-drawer__skip'))
        : [];
      const watchPlanSkipButton = watchPlanScreen?.querySelector('[data-watch-plan-skip]');
      const watchPlanHasOptions = Boolean(watchPlanScreen?.querySelector('[data-watch-plan-option]'));
      let watchPlanRequired = false;
      let showWatchPlanSkip = false;
      const productNode = entry.querySelector('[data-bundle-drawer-product]');
      const bundleProduct = parseJson(productNode?.textContent?.trim()) || {};
      const bundleImageUrlFromEntry = entry.getAttribute('data-bundle-image-url') || '';
      const bundleDisplayMode = resolveBundleDisplayMode(manifest);
      const flowMode = resolveFlowMode(manifest);
      const isUpsellFlow = flowMode === 'upsell';
      const groupUpsellInCart = resolveUpsellCartGrouping(manifest);
      const mobileSimOptinButton = mobilePlanScreen?.querySelector('[data-mobile-sim-optin]');
      const mobileSimVariantId = Number(mobileSimOptinButton?.getAttribute('data-mobile-sim-variant-id') || '0');
      const mobileOfferPercentageOff = resolveMobilePlanPercentageOff(manifest);
      const mobileOfferDurationMonths = resolveMobilePlanDiscountDuration(manifest);
      const mobileScreenConfig = findScreenConfig(manifest, 'mobile_plan') || {};
      const regiosProperties = resolveRegiosProperties(manifest, mobileOfferPercentageOff);
      const watchPlanDiscountProperties = resolveScreenDiscountProperties(manifest, 'watch_plan');
      const strapDiscountProperties = resolveScreenDiscountProperties(manifest, 'strap_selector');
      const accessoryDiscountProperties = resolveScreenDiscountProperties(manifest, 'accessory_selector');
      const includedAccessoryDiscountProperties = resolveIncludedAccessoryDiscountProperties(manifest);
      const accessoryScreenConfig = findScreenConfig(manifest, 'accessory_selector');
      const accessoryDiscountPercent = (() => {
        if (!accessoryScreenConfig || typeof accessoryScreenConfig !== 'object') {
          return null;
        }

        const candidates = [
          accessoryScreenConfig?.percentageOff,
          accessoryScreenConfig?.percentage_off,
          accessoryScreenConfig?.percentOff,
          accessoryScreenConfig?.percent_off,
          accessoryScreenConfig?.offPercentage,
          accessoryScreenConfig?.off_percentage,
          accessoryScreenConfig?.discountPercent,
          accessoryScreenConfig?.discount_percent,
          accessoryScreenConfig?.discountPercentage,
          accessoryScreenConfig?.discount_percentage
        ];

        for (const candidate of candidates) {
          const parsed = parseNonNegativePercent(candidate);
          if (parsed !== null) {
            return Math.max(0, Math.min(100, parsed));
          }
        }

        return null;
      })();
      const effectiveMobileOfferPercentageOff = mobileOfferPercentageOff;
      const parseBooleanFlag = (value, fallback = false) => {
        if (typeof value === 'boolean') {
          return value;
        }

        if (typeof value === 'string') {
          const normalized = value.trim().toLowerCase();
          if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
            return true;
          }

          if (normalized === 'false' || normalized === '0' || normalized === 'no') {
            return false;
          }
        }

        if (typeof value === 'number') {
          if (value === 1) {
            return true;
          }

          if (value === 0) {
            return false;
          }
        }

        return fallback;
      };
      const resolveWatchPlanRequired = () => {
        const watchPlanGroups = getManifestScreenGroups(manifest);

        for (const group of watchPlanGroups) {
          for (const screen of group) {
            if (!screen || typeof screen !== 'object') {
              continue;
            }

            const rawType = screen.type || screen.id || screen.screen || screen.key || screen.screenType || screen.name;
            if (canonicalScreenType(rawType) !== 'watch_plan') {
              continue;
            }

            const explicit = screen.required ?? screen.isRequired;
            if (explicit !== undefined && explicit !== null) {
              return parseBooleanFlag(explicit, false);
            }
          }
        }

        const watchPlanScreenConfig = findScreenConfig(manifest, 'watch_plan') || {};
        return parseBooleanFlag(
          watchPlanScreenConfig?.required ?? watchPlanScreenConfig?.isRequired,
          false
        );
      };
      watchPlanRequired = flow.hasType('watch_plan') && resolveWatchPlanRequired();
      showWatchPlanSkip = watchPlanScreen
        ? (!watchPlanRequired && watchPlanHasOptions)
        : false;
      const resolveMobilePlanRequired = () => {
        const mobileGroups = getManifestScreenGroups(manifest);

        for (const group of mobileGroups) {
          for (const screen of group) {
            if (!screen || typeof screen !== 'object') {
              continue;
            }

            const rawType = screen.type || screen.id || screen.screen || screen.key || screen.screenType || screen.name;
            if (canonicalScreenType(rawType) !== 'mobile_plan') {
              continue;
            }

            const explicit = screen.required ?? screen.isRequired;
            if (explicit !== undefined && explicit !== null) {
              return parseBooleanFlag(explicit, false);
            }
          }
        }

        return parseBooleanFlag(
          mobileScreenConfig?.required ?? mobileScreenConfig?.isRequired,
          false
        );
      };
      const mobilePlanRequired = resolveMobilePlanRequired();
      const mobilePlanAllowSkip = parseBooleanFlag(
        mobileScreenConfig?.allowSkip ?? mobileScreenConfig?.allow_skip,
        !mobilePlanRequired
      );
      const mobileOfferLabel = `${effectiveMobileOfferPercentageOff !== null ? effectiveMobileOfferPercentageOff : 0}%`;

      const resolveActivePdpVariant = () => {
        const activeVariantId = resolveActivePdpVariantId();
        if (latestPdpVariant && Number(latestPdpVariant.id) === activeVariantId) {
          return latestPdpVariant;
        }
        if (activeVariantId <= 0 || !Array.isArray(bundleProduct?.variants)) {
          return null;
        }

        return bundleProduct.variants.find((variant) => Number(variant?.id || 0) === activeVariantId) || null;
      };

      const resolveActivePdpVariantAvailable = (preferredVariant = null) => {
        if (preferredVariant && typeof preferredVariant === 'object' && 'available' in preferredVariant) {
          return preferredVariant.available !== false;
        }

        const activeVariant = resolveActivePdpVariant();
        if (activeVariant && 'available' in activeVariant) {
          return activeVariant.available !== false;
        }

        const activeForm = getActiveProductForm();
        if (activeForm) {
          const submitButton = activeForm.querySelector('.product-form__submit');
          if (submitButton) {
            return !submitButton.classList.contains('is-sold-out') && !submitButton.disabled;
          }
        }

        if (!activeVariant) {
          return true;
        }

        return activeVariant.available !== false;
      };

      const updateEntryTriggerState = (preferredVariant = null) => {
        if (!trigger) {
          return;
        }

        const entryRoot = trigger.closest('[data-bundle-drawer-entry]');
        const ctaRow = trigger.closest('[data-bundle-drawer-qty-control]');
        const triggerWrap = trigger.closest('.bundle-drawer-entry__trigger-wrap');
        let soldOut = !resolveActivePdpVariantAvailable(preferredVariant);
        // the parent bundle variant stays in stock, so the watch options are the real signal
        if (!soldOut && flow.hasType('variant_selector')) {
          soldOut = !hasAvailableWatchOptions();
        }
        if (entryRoot) {
          entryRoot.classList.remove('is-sold-out');
        }

        if (ctaRow) {
          ctaRow.classList.toggle('is-sold-out', soldOut);
        }

        if (triggerWrap) {
          triggerWrap.classList.toggle('is-sold-out', soldOut);
        }

        trigger.classList.toggle('is-sold-out', soldOut);
        trigger.disabled = soldOut;
        trigger.setAttribute('aria-disabled', soldOut ? 'true' : 'false');

        const triggerLabel = trigger.querySelector('.btn-text > span');
        if (triggerLabel) {
          triggerLabel.textContent = soldOut ? (triggerLabel.getAttribute('data-sold-out') || 'Out of Stock') : triggerDefaultLabel;
        }
      };

      const applySummarySectionOrder = () => {
        if (!summaryScreen || typeof flow?.orderedTypes !== 'function') {
          return;
        }

        const summaryList = summaryScreen.querySelector('.pud-partner__summary-list');
        if (!summaryList) {
          return;
        }

        const screenToSummarySection = {
          variant_selector: 'smartwatch',
          watch_plan: 'watch-sim',
          strap_selector: 'watch-straps',
          accessory_selector: 'accessories',
          mobile_plan: 'mobile-plan'
        };
        const defaultSectionTitles = {
          smartwatch: 'Smartwatch',
          'watch-sim': 'Watch SIM Plan',
          'watch-straps': 'Watch Strap',
          accessories: 'Accessories',
          'mobile-plan': 'Mobile Plan'
        };

        const resolveSummaryLabelForScreenType = (screenType, fallback) => {
          const config = findScreenConfig(manifest, screenType);
          if (!config || typeof config !== 'object') {
            return fallback;
          }

          const candidates = [
            config.summaryLabel,
            config.summary_label,
            config.summaryTitle,
            config.summary_title,
            config.heading,
            config.title,
            config.label,
            config.displayName,
            config.display_name
          ];

          for (const candidate of candidates) {
            if (typeof candidate === 'string' && candidate.trim()) {
              return candidate.trim();
            }
          }

          return fallback;
        };

        const sectionsByKey = new Map(
          Array.from(summaryList.querySelectorAll('[data-summary-section]')).map((section) => [section.getAttribute('data-summary-section'), section])
        );

        const orderedSectionKeys = [];
        flow.orderedTypes().forEach((screenType) => {
          const summaryKey = screenToSummarySection[screenType];
          if (!summaryKey || !sectionsByKey.has(summaryKey) || orderedSectionKeys.includes(summaryKey)) {
            return;
          }

          const section = sectionsByKey.get(summaryKey);
          if (section) {
            const resolvedTitle = resolveSummaryLabelForScreenType(screenType, defaultSectionTitles[summaryKey] || '');
            if (resolvedTitle) {
              const titleNode = section.querySelector('.g-summary-section-title');
              if (titleNode) {
                titleNode.textContent = resolvedTitle;
              }

              section.setAttribute('aria-label', `${resolvedTitle} summary`);

              const editLink = section.querySelector('.g-summary-edit-link');
              if (editLink) {
                editLink.setAttribute('aria-label', `Edit ${resolvedTitle}`);
              }
            }
          }

          orderedSectionKeys.push(summaryKey);
        });

        const leftoverSections = Array.from(sectionsByKey.keys()).filter((key) => !orderedSectionKeys.includes(key));
        [...orderedSectionKeys, ...leftoverSections].forEach((key) => {
          const section = sectionsByKey.get(key);
          if (section) {
            summaryList.appendChild(section);
          }
        });
      };
      applySummarySectionOrder();
      const formatMonthUnit = (months) => `${months} ${months === 1 ? 'month' : 'months'}`;
      const getMobileOfferDurationMonths = () => Math.max(1, Number(mobileOfferDurationMonths) || 1);
      const getMobileOfferDiscountPercent = () => Math.max(0, Math.min(100, Number(effectiveMobileOfferPercentageOff) || 0));

      const getMobileOfferHeadingText = () => {
        const discount = getMobileOfferDiscountPercent();
        const duration = getMobileOfferDurationMonths();

        if (discount === 100 && duration === 1) {
          return 'Switch your mobile plan and get the first month free.*';
        }

        if (duration > 1) {
          return `Switch your mobile plan and get ${discount}% off your first ${formatMonthUnit(duration)}.*`;
        }

        return `Switch your mobile plan and get ${discount}% off your first month.*`;
      };

      const getMobileDisclaimerPrefix = () => {
        const duration = getMobileOfferDurationMonths();
        if (duration > 1) {
          return `for the first ${formatMonthUnit(duration)},`;
        }

        return 'for the first month,';
      };

      const getMobileCtaDurationCopy = () => {
        const discount = getMobileOfferDiscountPercent();
        const duration = getMobileOfferDurationMonths();

        if (discount === 100 && duration === 1) {
          return 'first month free';
        }

        if (duration > 1) {
          return `first ${formatMonthUnit(duration)}`;
        }

        return 'first month';
      };

      const getSummaryMobileOfferDesc = () => {
        const discount = getMobileOfferDiscountPercent();
        const duration = getMobileOfferDurationMonths();

        if (discount === 100 && duration === 1) {
          return 'Add a mobile plan for your phone and your first month is free.';
        }

        if (duration > 1) {
          return `Add a mobile plan for your phone and your first ${formatMonthUnit(duration)} are ${discount}% off.`;
        }

        return `Add a mobile plan for your phone and your first month is ${discount}% off.`;
      };
      const isVariantOptionAvailable = (option) => {
        if (!option) {
          return false;
        }

        const availability = option.getAttribute('data-variant-available');
        if (availability !== null) {
          return availability === 'true';
        }

        return !option.disabled;
      };

      const resolveVariantOptionIndex = (options) => {
        if (!options.length) {
          return -1;
        }

        const selectedIndex = options.findIndex((option) => option.classList.contains('is-selected') && isVariantOptionAvailable(option));
        if (selectedIndex >= 0) {
          return selectedIndex;
        }

        const availableIndex = options.findIndex((option) => isVariantOptionAvailable(option));
        if (availableIndex >= 0) {
          return availableIndex;
        }

        return 0;
      };
      const userSelectionIntent = {
        watchPlan: false,
        mobilePlan: false,
        mobileSim: false
      };
      let watchPlanSkipped = false;
      let freeStrapSelectionOrder = 0;

      const initialSelectionState = {
        variantOptionIndex: (() => {
          const options = Array.from(overlay.querySelectorAll('[data-screen-type="variant_selector"] [data-variant-option]'));
          return resolveVariantOptionIndex(options);
        })(),
        watchPlanIndex: (() => {
          const options = Array.from(overlay.querySelectorAll('[data-watch-plan-screen] [data-watch-plan-option]'));
          const selectedIndex = options.findIndex((option) => option.classList.contains('is-selected'));
          return selectedIndex >= 0 ? selectedIndex : 0;
        })(),
        mobilePlanIndex: (() => {
          const options = Array.from(overlay.querySelectorAll('[data-screen-type="mobile_plan"] [data-mobile-plan-option]'));
          const selectedIndex = options.findIndex((option) => option.classList.contains('is-selected'));
          return selectedIndex >= 0 ? selectedIndex : 0;
        })(),
        mobileSimChecked: Boolean(mobilePlanScreen?.querySelector('[data-mobile-sim-optin]')?.classList.contains('is-checked')),
        summaryMobileOptinChecked: Boolean(summaryMobileOptin?.classList.contains('is-checked')),
        strapSelectedCards: new Set(Array.from(overlay.querySelectorAll('[data-screen-type="strap_selector"] [data-free-strap-card].is-selected'))),
        accessoryQtyByCard: new Map(
          Array.from(overlay.querySelectorAll('[data-screen-type="accessory_selector"] [data-accessory-addon-card]')).map((card) => [card, Number(card.getAttribute('data-qty') || '0')])
        )
      };

      const resolveActivePdpVariantId = () => {
        const eventVariantId = Number(latestPdpVariant?.id || '0');
        if (eventVariantId > 0) {
          return eventVariantId;
        }

        const picker = productInfo?.querySelector('variant-picker');
        const pickerVariant = parseJson(picker?.querySelector('[data-selected-variant]')?.textContent || '');
        const pickerVariantId = Number(pickerVariant?.id || '0');
        if (pickerVariantId > 0) {
          return pickerVariantId;
        }

        if (productFormId) {
          const activeForm = document.getElementById(productFormId);
          if (activeForm) {
            const activeInput = activeForm.querySelector('[name="id"]:not([type="radio"]), [name="id"]:checked');
            if (activeInput) {
              let value = '';

              if (activeInput.tagName === 'SELECT') {
                value = activeInput.value || '';
              } else if (activeInput.tagName === 'INPUT') {
                if (activeInput.type === 'radio') {
                  if (activeInput.checked) {
                    value = activeInput.value || '';
                  }
                } else {
                  value = activeInput.value || '';
                }
              }

              const parsed = Number(value || '0');
              if (parsed > 0) {
                return parsed;
              }
            }
          }
        }

        const urlVariantId = Number(new URLSearchParams(window.location.search).get('variant') || '0');
        if (urlVariantId > 0) {
          return urlVariantId;
        }

        const selectors = [
          '[data-product-form] [name="id"]',
          'form[action*="/cart/add"] [name="id"]',
          'product-form [name="id"]'
        ];

        for (const selector of selectors) {
          const scopeCandidates = [entry, document];

          for (const scope of scopeCandidates) {
            const nodes = Array.from(scope.querySelectorAll(selector));

            for (const node of nodes) {
              if (!node) {
                continue;
              }

              let value = '';

              if (node.tagName === 'SELECT') {
                value = node.value || '';
              } else if (node.tagName === 'INPUT') {
                if (node.type === 'radio') {
                  if (!node.checked) {
                    continue;
                  }
                  value = node.value || '';
                } else {
                  value = node.value || '';
                }
              }

              const parsed = Number(value || '0');
              if (parsed > 0) {
                return parsed;
              }
            }
          }
        }

        return 0;
      };

      const syncInitialVariantSelectionFromPdp = () => {
        const activeVariantId = resolveActivePdpVariantId();
        if (activeVariantId <= 0) {
          return;
        }

        const variantOptions = Array.from(overlay.querySelectorAll('[data-screen-type="variant_selector"] [data-variant-option]'));
        const matchedIndex = variantOptions.findIndex((option) => Number(option.getAttribute('data-variant-id') || '0') === activeVariantId && isVariantOptionAvailable(option));

        if (matchedIndex >= 0) {
          initialSelectionState.variantOptionIndex = matchedIndex;
        }
      };

      const hasMobilePlanUpsell = manifestIncludesScreenType(manifest, 'mobile_plan') || flow.hasType('mobile_plan');
      const shouldAutoSubmitOnClose = isUpsellFlow || bundleDisplayMode === 'default';
      const shouldShowMobilePlanInSummary = (() => {
        if (!hasMobilePlanUpsell) {
          return false;
        }

        const orderedTypes = flow.orderedTypes();
        const summaryIndex = orderedTypes.indexOf('order_summary');
        const mobileIndex = orderedTypes.indexOf('mobile_plan');

        if (summaryIndex < 0 || mobileIndex < 0) {
          return false;
        }

        return mobileIndex <= summaryIndex;
      })();

      if (summaryMobileOptin) {
        summaryMobileOptin.style.display = shouldShowMobilePlanInSummary ? 'block' : 'none';
        summaryMobileOptin.setAttribute('aria-hidden', shouldShowMobilePlanInSummary ? 'false' : 'true');
      }

      const hasAvailableWatchOptions = () => {
        if (!flow.hasType('variant_selector')) {
          return resolveActivePdpVariantAvailable();
        }

        const variantScreen = overlay.querySelector('[data-screen-type="variant_selector"]');
        if (!variantScreen) {
          return false;
        }

        const options = Array.from(variantScreen.querySelectorAll('[data-variant-option]'));
        return options.some((option) => {
          const availability = option.getAttribute('data-variant-available');
          return availability !== 'false' && !option.disabled;
        });
      };

      const setSummaryCtaSoldOutState = (soldOut) => {
        if (!summaryCtaButton) {
          return;
        }

        if (soldOut) {
          summaryCtaButton.classList.add('is-sold-out');
          summaryCtaButton.disabled = true;
          summaryCtaButton.setAttribute('aria-disabled', 'true');
          summaryCtaButton.textContent = 'Out of Stock';
        } else {
          summaryCtaButton.classList.remove('is-sold-out');
          summaryCtaButton.disabled = false;
          summaryCtaButton.setAttribute('aria-disabled', 'false');
          summaryCtaButton.textContent = 'Add to Cart';
        }
      };

      const updateSummaryCtaState = () => {
        setSummaryCtaSoldOutState(!hasAvailableWatchOptions());
      };

      const setSummaryMobileOptinChecked = (checked) => {
        if (!summaryMobileOptin) {
          return;
        }

        summaryMobileOptin.classList.toggle('is-checked', checked);
        summaryMobileOptin.setAttribute('aria-pressed', checked ? 'true' : 'false');

        if (summaryMobileOptinLabel) {
          summaryMobileOptinLabel.textContent = checked ? "Yes, I'm interested" : 'No, thanks';
        }

        updateSummaryCtaState();
      };

      if (summaryMobileOptin && shouldShowMobilePlanInSummary) {
        setSummaryMobileOptinChecked(summaryMobileOptin.classList.contains('is-checked'));
      } else {
        updateSummaryCtaState();
      }

      updateEntryTriggerState();

      if (mobilePlanRequired && summaryMobileOptin && shouldShowMobilePlanInSummary) {
        setSummaryMobileOptinChecked(true);
        summaryMobileOptin.style.display = 'none';
        summaryMobileOptin.setAttribute('aria-hidden', 'true');
      }

      if (summaryMobileOfferTitle) {
        summaryMobileOfferTitle.textContent = shouldShowMobilePlanInSummary
          ? `You've unlocked ${mobileOfferLabel} off Spacetalk Mobile`
          : '';
      }

      if (summaryMobileOfferDesc) {
        summaryMobileOfferDesc.textContent = shouldShowMobilePlanInSummary
          ? getSummaryMobileOfferDesc()
          : '';
      }

      if (mobilePlanOfferHeading) {
        mobilePlanOfferHeading.textContent = getMobileOfferHeadingText();
      }

      // Allow manifest screen config to control free strap allowance.
      if (strapAllowance) {
        const configuredAllowance = resolveStrapFreeAllowance(manifest);
        if (configuredAllowance !== null) {
          strapAllowance.setAttribute('data-free-strap-allowance', String(configuredAllowance));
        }
      }

      const formatPriceFromCents = (cents) => {
        const amount = Number(cents) || 0;
        return formatMoneyCents(amount);
      };

      const parsePriceToCents = (value) => {
        if (value === null || value === undefined) {
          return 0;
        }

        const normalized = String(value).replace(/[^0-9.-]/g, '');
        if (!normalized) {
          return 0;
        }

        const amount = Number(normalized);
        if (!Number.isFinite(amount)) {
          return 0;
        }

        return Math.round(amount * 100);
      };

      const resolveStrapPriceCents = (card) => {
        if (!card) {
          return 0;
        }

        const cents = Number(card.getAttribute('data-price-cents') || '0');
        return Number.isFinite(cents) && cents > 0 ? Math.round(cents) : 0;
      };

      const resolveVariantPriceCents = (option) => {
        if (!option) {
          return 0;
        }

        const direct = Number(option.getAttribute('data-variant-price-cents') || '0');
        if (Number.isFinite(direct) && direct > 0) {
          return Math.round(direct);
        }

        return resolveStrapPriceCents(option);
      };

      const resolveVariantRrpCents = (option, fallbackPriceCents = 0) => {
        if (!option) {
          return Math.max(0, Number(fallbackPriceCents) || 0);
        }

        const compareAt = Number(option.getAttribute('data-variant-compare-at-cents') || '0');
        const fallback = Math.max(0, Number(fallbackPriceCents) || 0);

        if (Number.isFinite(compareAt) && compareAt > 0) {
          return Math.max(Math.round(compareAt), fallback);
        }

        return fallback;
      };

      const resolveItemRrpCents = (card, fallbackPriceCents = 0) => {
        if (!card) {
          return Math.max(0, Number(fallbackPriceCents) || 0);
        }

        const rrp = Number(card.getAttribute('data-rrp-cents') || '0');
        const fallback = Math.max(0, Number(fallbackPriceCents) || 0);
        if (Number.isFinite(rrp) && rrp > 0) {
          return Math.max(Math.round(rrp), fallback);
        }

        return fallback;
      };

      const resolveAccessoryPriceCents = (card) => {
        if (!card) {
          return 0;
        }

        const cents = Number(card.getAttribute('data-price-cents') || '0');
        return Number.isFinite(cents) && cents > 0 ? Math.round(cents) : 0;
      };

      const appendSummaryRow = (container, options) => {
        const row = document.createElement('div');
        row.className = 'pud-partner__summary-row';
        row.setAttribute('data-summary-item', '');

        const info = document.createElement('div');
        info.className = 'pud-partner__summary-info';

        const name = document.createElement('p');
        name.className = 'pud-partner__summary-name';
        name.textContent = options.name || '';

        if (options.qty && options.qty > 0) {
          const qty = document.createElement('span');
          qty.className = 'g-summary-qty';
          qty.textContent = `x${options.qty}`;
          name.appendChild(document.createTextNode(' '));
          name.appendChild(qty);
        }

        info.appendChild(name);

        const end = document.createElement('div');
        end.className = 'pud-partner__summary-row-end';

        const price = document.createElement('p');
        price.className = options.isFree
          ? 'pud-partner__summary-price pud-partner__summary-price--free'
          : 'pud-partner__summary-price';
        if (options.priceHtml) {
          price.innerHTML = options.priceHtml;
        } else {
          price.textContent = options.priceText || formatPriceFromCents(0);
        }

        end.appendChild(price);
        row.appendChild(info);
        row.appendChild(end);
        container.appendChild(row);
      };

      const updateOrderSummary = () => {
        if (!summaryScreen || !summarySubtotal || !summaryAddons || !summaryTotal) {
          return;
        }

        const sectionContainers = {
          smartwatch: summaryScreen.querySelector('[data-summary-section="smartwatch"] [data-summary-items]'),
          watchSim: summaryScreen.querySelector('[data-summary-section="watch-sim"] [data-summary-items]'),
          straps: summaryScreen.querySelector('[data-summary-section="watch-straps"] [data-summary-items]'),
          mobilePlan: summaryScreen.querySelector('[data-summary-section="mobile-plan"] [data-summary-items]'),
          accessories: summaryScreen.querySelector('[data-summary-section="accessories"] [data-summary-items]')
        };

        Object.values(sectionContainers).forEach((container) => {
          if (container) {
            container.innerHTML = '';
          }
        });

        let subtotalCents = 0;
        let addonsCents = 0;
        let baseRrpCents = 0;

        const variantScreen = overlay.querySelector('[data-screen-type="variant_selector"]');
        const selectedVariant = getSelectedVariantOption();
        if (selectedVariant && sectionContainers.smartwatch) {
          const colour = selectedVariant.getAttribute('data-watch-summary-colour') || selectedVariant.getAttribute('data-colour') || '';
          const baseName = selectedVariant.getAttribute('data-watch-summary-name') || (variantScreen?.querySelector('.pud-partner__heading--variant')?.textContent || 'Smartwatch').trim();
          const watchName = colour && !baseName.toLowerCase().includes(colour.toLowerCase()) ? `${baseName} - ${colour}` : baseName;
          const watchPrice = resolveVariantPriceCents(selectedVariant);
          const watchRrp = resolveVariantRrpCents(selectedVariant, watchPrice);

          subtotalCents += watchPrice;
          baseRrpCents += watchRrp;

          appendSummaryRow(sectionContainers.smartwatch, {
            name: watchName,
            isFree: true,
            priceText: 'Included'
          });
        } else if (!flow.hasType('variant_selector') && sectionContainers.smartwatch) {
          const pdpVariant = resolveActivePdpVariant();
          if (pdpVariant) {
            const variantTitle = pdpVariant.title === 'Default Title' ? '' : pdpVariant.title || '';
            const watchName = variantTitle ? `${bundleProduct.title} - ${variantTitle}` : bundleProduct.title;
            const watchPrice = Number(pdpVariant.price || 0);
            subtotalCents += watchPrice;
            baseRrpCents += Math.max(Number(pdpVariant.compare_at_price || 0), watchPrice);
            appendSummaryRow(sectionContainers.smartwatch, {
              name: watchName,
              isFree: true,
              priceText: 'Included'
            });
          }
        }

        const watchPlanScreen = overlay.querySelector('[data-watch-plan-screen]');
        const selectedPlan = watchPlanScreen?.querySelector('[data-watch-plan-option].is-selected') || watchPlanScreen?.querySelector('[data-watch-plan-option]');
        if (selectedPlan && sectionContainers.watchSim) {
          const planName = selectedPlan.getAttribute('data-plan-name') || 'Watch plan';
          const planPriceAttr = selectedPlan.getAttribute('data-plan-price-cents');
          const planPrice = planPriceAttr !== null && Number.isFinite(Number(planPriceAttr))
            ? Math.round(Number(planPriceAttr))
            : parsePriceToCents(selectedPlan.getAttribute('data-plan-price'));
          const planRrpAttr = Number(selectedPlan.getAttribute('data-plan-rrp-cents') || '0');
          const planRrp = Number.isFinite(planRrpAttr) && planRrpAttr > 0 ? Math.max(Math.round(planRrpAttr), planPrice) : planPrice;

          subtotalCents += planPrice;
          baseRrpCents += planRrp;

          appendSummaryRow(sectionContainers.watchSim, {
            name: planName,
            isFree: true,
            priceText: `Included - ${formatPriceFromCents(planPrice)}`
          });
        }

        if (strapScreen && sectionContainers.straps) {
          const allowanceRaw = Number(strapAllowance?.getAttribute('data-free-strap-allowance'));
          const allowance = Number.isFinite(allowanceRaw) && allowanceRaw >= 0 ? Math.floor(allowanceRaw) : 0;

          const selectedStraps = Array.from(strapScreen.querySelectorAll('[data-free-strap-card].is-selected')).sort(
            (left, right) => Number(left.getAttribute('data-free-strap-rank') || '0') - Number(right.getAttribute('data-free-strap-rank') || '0')
          );

          selectedStraps.forEach((card, index) => {
            const strapName = card.getAttribute('data-name') || 'Strap';
            const strapPrice = resolveStrapPriceCents(card);
            const strapRrp = resolveItemRrpCents(card, strapPrice);
            const isFree = index < allowance;

            if (!isFree) {
              addonsCents += strapPrice;
            } else {
              baseRrpCents += strapRrp;
            }

            appendSummaryRow(sectionContainers.straps, {
              name: strapName,
              qty: 1,
              isFree,
              priceText: isFree ? 'Free' : `+${formatPriceFromCents(strapPrice)}`
            });
          });
        }

        if (sectionContainers.mobilePlan && shouldShowMobilePlanInSummary) {
          const selectedMobilePlan = getSelectedMobilePlanCard({ requireIntent: false });

          if (selectedMobilePlan) {
            const mobileName = (selectedMobilePlan.querySelector('.plan-drawer__deal-name')?.textContent || '').trim() || selectedMobilePlan.getAttribute('data-mobile-plan-name') || 'Mobile Plan';
            const mobilePriceText = (selectedMobilePlan.querySelector('.plan-drawer__deal-price-now')?.textContent || '').trim();
            const mobilePriceCents = parseCurrencyTextToCents(mobilePriceText);
            const isFreeMobilePlan = mobilePriceCents <= 0 || /free/i.test(mobilePriceText);

            if (!isFreeMobilePlan) {
              addonsCents += mobilePriceCents;
            }

            appendSummaryRow(sectionContainers.mobilePlan, {
              name: mobileName,
              qty: 1,
              isFree: isFreeMobilePlan,
              priceText: isFreeMobilePlan ? 'Free' : `+${formatPriceFromCents(mobilePriceCents)}`
            });
          }
        }

        if (sectionContainers.accessories) {
          const includeIncluded = includedAccessories && includedAccessories.style.display !== 'none';

          if (includeIncluded) {
            includedAccessoryCards.forEach((card) => {
              const name = card.getAttribute('data-name') || 'Accessory';
              const price = resolveAccessoryPriceCents(card);
              const rrp = resolveItemRrpCents(card, price);

              appendSummaryRow(sectionContainers.accessories, {
                name,
                qty: 1,
                isFree: true,
                priceText: 'Included'
              });

              baseRrpCents += rrp;
            });
          }

          if (accessoryGrid) {
            Array.from(accessoryGrid.querySelectorAll('[data-accessory-addon-card]')).forEach((card) => {
              const qty = Math.max(0, Math.floor(Number(card.getAttribute('data-qty') || '0')));
              if (qty < 1) {
                return;
              }

              const name = card.getAttribute('data-name') || 'Accessory';
              const unitPrice = resolveAccessoryPriceCents(card);
              const unitRrp = resolveItemRrpCents(card, unitPrice);
              const discountedUnitPrice = accessoryDiscountPercent !== null && accessoryDiscountPercent > 0
                ? Math.max(0, Math.round(unitPrice * (100 - accessoryDiscountPercent) / 100))
                : unitPrice;
              const linePrice = discountedUnitPrice * qty;
              const lineRrp = unitRrp * qty;

              addonsCents += linePrice;
              // Paid add-ons are intentionally excluded from base bundle RRP.

              appendSummaryRow(sectionContainers.accessories, {
                name,
                qty,
                priceHtml: accessoryDiscountPercent !== null && accessoryDiscountPercent > 0 && accessoryDiscountPercent < 100
                  ? `<s>+${formatPriceFromCents(unitPrice * qty)}</s> +${formatPriceFromCents(linePrice)}`
                  : undefined,
                priceText: `+${formatPriceFromCents(linePrice)}`
              });
            });
          }
        }

        Object.entries({
          smartwatch: sectionContainers.smartwatch,
          'watch-sim': sectionContainers.watchSim,
          'watch-straps': sectionContainers.straps,
          'mobile-plan': sectionContainers.mobilePlan,
          accessories: sectionContainers.accessories
        }).forEach(([sectionKey, container]) => {
          const section = summaryScreen.querySelector(`[data-summary-section="${sectionKey}"]`);
          if (!section || !container) {
            return;
          }

          section.style.display = container.children.length > 0 ? '' : 'none';
        });

        const totalCents = subtotalCents + addonsCents;
        summarySubtotal.textContent = formatPriceFromCents(subtotalCents);
        summaryAddons.textContent = addonsCents > 0 ? `+${formatPriceFromCents(addonsCents)}` : formatPriceFromCents(0);
        summaryTotal.textContent = formatPriceFromCents(totalCents);

        if (summaryRrpBlurb) {
          const savingsCents = baseRrpCents - subtotalCents;
          if (savingsCents > 0) {
            summaryRrpBlurb.hidden = false;
            summaryRrpBlurb.textContent = `Bundle RRP: ${formatPriceFromCents(baseRrpCents)}. You save ${formatPriceFromCents(savingsCents)} with this bundle.`;
          } else {
            summaryRrpBlurb.hidden = true;
            summaryRrpBlurb.textContent = '';
          }
        }
      };

      const updateFreeStrapState = () => {
        if (!strapScreen || !strapAllowance) {
          return;
        }

        const allowanceRaw = Number(strapAllowance.getAttribute('data-free-strap-allowance'));
        const limit = Number.isFinite(allowanceRaw) && allowanceRaw >= 0 ? Math.floor(allowanceRaw) : 0;
        const minimumRequired = Math.max(limit, 1);

        if (strapTitle) {
          strapTitle.textContent = limit === 0 ? 'Add on extra watch straps' : 'Select your strap';
        }

        if (strapSub) {
          strapSub.textContent = 'Choose from a variety of colours.';
        }

        const selectedCards = Array.from(strapScreen.querySelectorAll('[data-free-strap-card].is-selected')).sort(
          (left, right) => Number(left.getAttribute('data-free-strap-rank') || '0') - Number(right.getAttribute('data-free-strap-rank') || '0')
        );
        const selectedCount = selectedCards.length;
        const paidCards = selectedCards.slice(limit);
        const extraTotal = paidCards.reduce((total, card) => total + resolveStrapPriceCents(card), 0);

        if (selectedCount === 0) {
          strapAllowance.textContent = limit > 0
            ? (limit === 1 ? 'Choose 1 strap for free' : `Choose ${limit} straps for free`)
            : 'No free straps included';
        } else if (extraTotal > 0) {
          const noun = selectedCount === 1 ? 'strap' : 'straps';
          strapAllowance.textContent = `${selectedCount} ${noun} selected · +${formatPriceFromCents(extraTotal)} extra`;
        } else {
          const noun = selectedCount === 1 ? 'strap' : 'straps';
          strapAllowance.textContent = `${selectedCount} ${noun} selected · Free`;
        }

        const freeCards = selectedCards.slice(0, limit);
        strapScreen.querySelectorAll('[data-free-strap-card]').forEach((card) => {
          const priceEl = card.querySelector('[data-free-strap-price]');

          if (priceEl) {
            if (selectedCount < limit) {
              priceEl.textContent = '';
            } else if (freeCards.includes(card)) {
              priceEl.textContent = 'FREE';
            } else {
              priceEl.textContent = `+${formatPriceFromCents(resolveStrapPriceCents(card))}`;
            }
          }

          card.setAttribute('aria-pressed', card.classList.contains('is-selected') ? 'true' : 'false');
        });

        if (strapCta) {
          if (limit === 0) {
            strapCta.disabled = false;
            strapCta.textContent = selectedCount === 0
              ? 'Skip extra add-on straps'
              : `Continue with ${selectedCount} ${selectedCount === 1 ? 'strap' : 'straps'}${extraTotal > 0 ? ` (+${formatPriceFromCents(extraTotal)})` : ''}`;
          } else {
            strapCta.disabled = selectedCount < minimumRequired;
            strapCta.textContent = selectedCount < minimumRequired
              ? `Select ${minimumRequired} ${minimumRequired === 1 ? 'strap' : 'straps'} to continue`
              : `Continue with ${selectedCount} ${selectedCount === 1 ? 'strap' : 'straps'}${extraTotal > 0 ? ` (+${formatPriceFromCents(extraTotal)})` : ''}`;
          }
        }
      };

      const updateAccessoryState = () => {
        if (!accessoryScreen || !accessorySummary || !accessoryGrid || !accessoryCta) {
          return;
        }

        const includedCount = includedAccessoryCards.length;
        if (includedAccessories) {
          includedAccessories.style.display = includedCount > 0 ? '' : 'none';
        }

        includedAccessoryCards.forEach((card) => {
          const priceEl = card.querySelector('[data-accessory-price]');
          if (priceEl) {
            priceEl.textContent = 'Included';
          }
        });

        const cards = Array.from(accessoryGrid.querySelectorAll('[data-accessory-addon-card]'));
        let totalQty = 0;
        let totalCost = 0;

        cards.forEach((card) => {
          let qty = Number(card.getAttribute('data-qty') || '0');
          if (!Number.isFinite(qty) || qty < 0) {
            qty = 0;
          }

          qty = Math.min(99, Math.floor(qty));
          card.setAttribute('data-qty', String(qty));

          const qtyEl = card.querySelector('[data-accessory-qty]');
          const addBtn = card.querySelector('[data-accessory-qty-add]');
          const decBtn = card.querySelector('[data-accessory-qty-dec]');
          const incBtn = card.querySelector('[data-accessory-qty-inc]');
          const priceEl = card.querySelector('[data-accessory-price]');
          const unitPrice = resolveAccessoryPriceCents(card);
          const hasAccessoryDiscount = accessoryDiscountPercent !== null && accessoryDiscountPercent > 0;
          const discountedUnitPrice = hasAccessoryDiscount
            ? Math.round(unitPrice * (100 - accessoryDiscountPercent) / 100)
            : unitPrice;

          if (priceEl) {
            if (hasAccessoryDiscount && accessoryDiscountPercent < 100) {
              priceEl.innerHTML = `<s>+${formatPriceFromCents(unitPrice)}</s> +${formatPriceFromCents(discountedUnitPrice)}`;
            } else if (hasAccessoryDiscount && accessoryDiscountPercent >= 100) {
              priceEl.textContent = 'FREE';
            } else {
              priceEl.textContent = `+${formatPriceFromCents(unitPrice)}`;
            }
          }

          if (qtyEl) {
            qtyEl.textContent = String(qty);
          }

          if (addBtn) {
            addBtn.style.display = qty === 0 ? 'inline-flex' : 'none';
          }

          if (decBtn) {
            decBtn.disabled = qty === 0;
          }

          if (incBtn) {
            incBtn.disabled = qty >= 99;
          }

          const selected = qty > 0;
          card.classList.toggle('is-selected', selected);
          card.setAttribute('aria-pressed', selected ? 'true' : 'false');

          totalQty += qty;
          totalCost += qty * discountedUnitPrice;
        });

        if (accessoryEmpty) {
          accessoryEmpty.style.display = cards.length === 0 ? 'block' : 'none';
        }

        if (totalQty === 0) {
          accessorySummary.textContent = '0 extra accessories added';
        } else if (totalQty === 1) {
          accessorySummary.textContent = `1 extra accessory added · +${formatPriceFromCents(totalCost)}`;
        } else {
          accessorySummary.textContent = `${totalQty} extra accessories added · +${formatPriceFromCents(totalCost)}`;
        }

        accessoryCta.disabled = cards.length === 0 && includedCount === 0;
        const isAccessoryLast = flow.isLast() && flow.activeType() === 'accessory_selector';
        if (isAccessoryLast) {
          accessoryCta.textContent = totalQty === 0
            ? 'Skip accessories & Add to Cart'
            : 'Continue with accessories & Add to Cart';
        } else if (totalQty === 0 && includedCount === 0) {
          accessoryCta.textContent = 'Skip Accessories';
        } else {
          accessoryCta.textContent = 'Continue with accessories';
        }

        updateOrderSummary();
      };

      const parseCurrencyTextToCents = (text) => {
        if (!text) {
          return 0;
        }

        const normalized = String(text).replace(/[^0-9.-]/g, '');
        if (!normalized) {
          return 0;
        }

        const amount = Number(normalized);
        if (!Number.isFinite(amount)) {
          return 0;
        }

        return Math.round(amount * 100);
      };

      const formatCentsToPrice = (cents) => formatMoneyCents(cents);

      const updateMobilePlanPricesFromDiscount = () => {
        if (!mobilePlanScreen || effectiveMobileOfferPercentageOff === null) {
          return;
        }

        const percentageOff = Math.max(0, Math.min(100, effectiveMobileOfferPercentageOff));
        const planCards = Array.from(mobilePlanScreen.querySelectorAll('.plan-drawer__deal-card.plan-drawer__plan'));

        planCards.forEach((card) => {
          const wasPriceEl = card.querySelector('.plan-drawer__deal-price-was');
          const nowPriceEl = card.querySelector('.plan-drawer__deal-price-now');
          const unitEl = card.querySelector('.plan-drawer__deal-price-unit');
          const noteEl = card.querySelector('.plan-drawer__deal-price-note');
          const noteStrongEl = card.querySelector('.plan-drawer__deal-price-note strong');

          if (!wasPriceEl || !nowPriceEl) {
            return;
          }

          const baseCents = parseCurrencyTextToCents(wasPriceEl.textContent);
          if (baseCents <= 0) {
            return;
          }

          const discountedCents = Math.round(baseCents * (100 - percentageOff) / 100);
          if (discountedCents <= 0) {
            nowPriceEl.textContent = 'FREE';
            if (wasPriceEl) {
              wasPriceEl.style.display = 'none';
              wasPriceEl.style.removeProperty('text-decoration');
            }
            if (unitEl) {
              unitEl.style.display = 'none';
            }
          } else {
            nowPriceEl.textContent = formatCentsToPrice(discountedCents);
            if (wasPriceEl) {
              wasPriceEl.style.display = '';
            }
            if (unitEl) {
              unitEl.style.display = '';
            }
          }

          if (noteEl) {
            noteEl.innerHTML = `${getMobileDisclaimerPrefix()}<br><strong>${formatCentsToPrice(baseCents)} thereafter*</strong>`;
          }

          if (noteStrongEl) {
            noteStrongEl.textContent = `${formatCentsToPrice(baseCents)} thereafter*`;
          }
        });
      };

      const setMobileSimOptinChecked = (checked) => {
        if (!mobilePlanScreen) {
          return;
        }

        const mobileSimOptin = mobilePlanScreen.querySelector('[data-mobile-sim-optin]');
        if (!mobileSimOptin) {
          return;
        }

        mobileSimOptin.classList.toggle('is-checked', checked);
        mobileSimOptin.setAttribute('aria-pressed', checked ? 'true' : 'false');
      };

      const getSelectedVariantOption = () => {
        if (!flow.hasType('variant_selector')) {
          return null;
        }

        const variantScreen = overlay.querySelector('[data-screen-type="variant_selector"]');
        if (!variantScreen) {
          return null;
        }

        return variantScreen.querySelector('[data-variant-option].is-selected')
          || variantScreen.querySelector('[data-variant-option]:not([data-variant-available="false"]):not(:disabled)');
      };

      const resolveBaseWatchVariantId = () => {
        const variantScreen = flow.hasType('variant_selector') ? overlay.querySelector('[data-screen-type="variant_selector"]') : null;
        const variantOptions = variantScreen ? Array.from(variantScreen.querySelectorAll('[data-variant-option]')) : [];
        if (variantOptions.length && !variantOptions.some((option) => isVariantOptionAvailable(option))) {
          return 0;
        }

        const selectedVariant = getSelectedVariantOption();
        const selectedVariantId = Number(selectedVariant?.getAttribute('data-variant-id') || '0');
        if (selectedVariantId > 0) {
          return selectedVariantId;
        }

        const activeVariantId = resolveActivePdpVariantId();
        if (activeVariantId > 0) {
          return activeVariantId;
        }

        const fallbackCandidates = [
          bundleProduct?.selected_or_first_available_variant?.id,
          bundleProduct?.selectedOrFirstAvailableVariant?.id,
          Array.isArray(bundleProduct?.variants) ? bundleProduct.variants[0]?.id : null
        ];

        for (const candidate of fallbackCandidates) {
          const parsed = Number(candidate || '0');
          if (parsed > 0) {
            return parsed;
          }
        }

        return 0;
      };

      const getSelectedPlanCard = ({ requireIntent = false } = {}) => {
        if (!watchPlanScreen) {
          return null;
        }

        if (watchPlanSkipped) {
          return null;
        }

        if (requireIntent && !watchPlanRequired && !userSelectionIntent.watchPlan) {
          return null;
        }

        return watchPlanScreen.querySelector('[data-watch-plan-option].is-selected') || watchPlanScreen.querySelector('[data-watch-plan-option]');
      };

      const getSelectedMobilePlanCard = ({ requireIntent = false } = {}) => {
        if (!mobilePlanScreen) {
          return null;
        }

        if (requireIntent && !userSelectionIntent.mobilePlan) {
          return null;
        }

        return mobilePlanScreen.querySelector('[data-mobile-plan-option].is-selected') || mobilePlanScreen.querySelector('.plan-drawer__deal-card.plan-drawer__plan.is-selected');
      };

      const getSelectedStrapCards = () => {
        if (!strapScreen) {
          return [];
        }

        return Array.from(strapScreen.querySelectorAll('[data-free-strap-card].is-selected')).sort(
          (left, right) => Number(left.getAttribute('data-free-strap-rank') || '0') - Number(right.getAttribute('data-free-strap-rank') || '0')
        );
      };

      const getSelectedAccessoryCards = () => {
        if (!accessoryScreen) {
          return [];
        }

        const cards = [];

        if (includedAccessories && includedAccessories.style.display !== 'none') {
          cards.push(...Array.from(accessoryScreen.querySelectorAll('[data-included-accessory-card]')));
        }

        if (accessoryGrid) {
          cards.push(...Array.from(accessoryGrid.querySelectorAll('[data-accessory-addon-card]')).filter((card) => Number(card.getAttribute('data-qty') || '0') > 0));
        }

        return cards;
      };

      const getSelectedMobileSimChecked = () => {
        if (!mobilePlanScreen) {
          return false;
        }

        return mobilePlanScreen.querySelector('[data-mobile-sim-optin]')?.classList.contains('is-checked') || false;
      };

      const formatOfferPercent = (value) => `${Math.max(0, Math.min(100, Number(value) || 0))}%`;

      const ensureSubmittingLayer = () => {
        let layer = overlay.querySelector('[data-bundle-drawer-submitting-layer]');
        if (layer) {
          return layer;
        }

        const dialog = overlay.querySelector('[data-bundle-drawer-dialog]') || overlay;
        layer = document.createElement('div');
        layer.className = 'bundle-drawer__submitting-layer';
        layer.setAttribute('data-bundle-drawer-submitting-layer', '');
        layer.setAttribute('hidden', '');
        layer.innerHTML = [
          '<div class="bundle-drawer__submitting-panel" role="status" aria-live="polite">',
          '  <svg class="bundle-drawer__submitting-spinner icon-rotator animate-rotator" viewBox="0 0 66 66" stroke="currentColor" fill="none" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">',
          '    <circle class="path" stroke-width="6" cx="33" cy="33" r="30"></circle>',
          '  </svg>',
          '  <p class="bundle-drawer__submitting-title">Please wait... adding to cart</p>',
          '</div>'
        ].join('');

        dialog.appendChild(layer);
        return layer;
      };

      const setSubmittingLayerVisible = (visible) => {
        const layer = ensureSubmittingLayer();

        if (visible) {
          submittingShownAt = Date.now();
          layer.removeAttribute('hidden');
        } else {
          layer.setAttribute('hidden', '');
        }
      };

      const resolveBundleUrl = () => {
        const candidates = [
          manifest?.bundleUrl,
          manifest?.bundle_url,
          manifest?.rules?.bundleUrl,
          manifest?.rules?.bundle_url,
          bundleProduct?.url,
          bundleProduct?.online_store_url,
          bundleProduct?.onlineStoreUrl,
          bundleProduct?.handle ? `/products/${bundleProduct.handle}` : '',
          window.location.pathname
        ];

        for (const candidate of candidates) {
          if (typeof candidate === 'string' && candidate.trim()) {
            return candidate.trim();
          }
        }

        return '';
      };

      const resolveBundleImageUrl = (watchImage) => {
        const featuredImage = bundleProduct?.featured_image;
        const productFeaturedImageUrl = typeof featuredImage === 'string'
          ? featuredImage
          : featuredImage?.url || featuredImage?.src || '';

        const bundleImageCandidates = [
          manifest?.bundleImageUrl,
          manifest?.bundle_image_url,
          manifest?.bundleImage,
          manifest?.bundle_image,
          manifest?.rules?.bundleImageUrl,
          manifest?.rules?.bundle_image_url,
          bundleImageUrlFromEntry,
          productFeaturedImageUrl
        ];

        const candidates = bundleDisplayMode === 'bundle'
          ? [...bundleImageCandidates, watchImage]
          : [watchImage, ...bundleImageCandidates];

        for (const candidate of candidates) {
          if (typeof candidate === 'string' && candidate.trim()) {
            return candidate.trim();
          }
        }

        return '';
      };

      const hasSelectedWatchPlan = ({ requireIntent = false } = {}) => Boolean(getSelectedPlanCard({ requireIntent }));
      const hasSelectedStraps = () => getSelectedStrapCards().length > 0;
      const hasSelectedAccessories = () => getSelectedAccessoryCards().length > 0;
      const hasSelectedMobilePlanForSubmit = ({ requireIntent = false } = {}) => Boolean(getSelectedMobilePlanCard({ requireIntent }));
      const shouldIncludeMobileOnClose = ({ requireIntent = true } = {}) => hasSelectedMobilePlanForSubmit({ requireIntent });

      const shouldSubmitAsPlainWatchOnly = ({ includeMobilePlan = false, closeout = false } = {}) => {
        const selectedVariant = getSelectedVariantOption();
        const selectedPlan = getSelectedPlanCard({ requireIntent: closeout });
        const selectedStraps = getSelectedStrapCards();
        const selectedAccessories = getSelectedAccessoryCards();
        const selectedMobilePlan = includeMobilePlan ? getSelectedMobilePlanCard({ requireIntent: closeout }) : null;

        const hasAnyUpsellSelection = hasSelectedWatchPlan({ requireIntent: closeout })
          || selectedStraps.length > 0
          || selectedAccessories.length > 0
          || (includeMobilePlan && Boolean(selectedMobilePlan));

        const watchVariantId = Number(selectedVariant?.getAttribute('data-variant-id') || '0') || resolveBaseWatchVariantId();

        return watchVariantId > 0
          && !selectedPlan
          && selectedStraps.length === 0
          && selectedAccessories.length === 0
          && !(includeMobilePlan && selectedMobilePlan)
          && !hasAnyUpsellSelection;
      };

      const getBundleProperties = (bundleId, options = {}) => {
        const hasUpsellSelections = Boolean(options.hasUpsellSelections);
        const selectedPlanForBundleProps = options.selectedPlan || null;

        if (isUpsellFlow && (!groupUpsellInCart || !hasUpsellSelections)) {
          return {};
        }

        const selectedVariant = getSelectedVariantOption();
        const pdpVariant = !flow.hasType('variant_selector') ? resolveActivePdpVariant() : null;
        const selectedPlan = selectedPlanForBundleProps;
        const watchColor = selectedVariant?.getAttribute('data-colour') || selectedVariant?.getAttribute('data-watch-summary-colour') || (pdpVariant?.title !== 'Default Title' ? pdpVariant?.title : '') || '';
        const watchImage = selectedVariant?.getAttribute('data-variant-image-url') || selectedVariant?.querySelector('img')?.getAttribute('src') || pdpVariant?.featured_image?.src || '';
        const bundleUrl = resolveBundleUrl();
        const bundleImage = resolveBundleImageUrl(watchImage);
        const strapLabels = getSelectedStrapCards().map((card) => card.getAttribute('data-name') || '').filter(Boolean);

        return {
          _bundle_instance: bundleId,
          _bundle_key: bundleProduct.title || bundleProduct.handle || 'bundle',
          _bundle_url: bundleUrl,
          _bundle_type: 'spacetalk',
          _bundle_display_mode: bundleDisplayMode,
          _bundle_image: bundleImage,
          _bundle_watch: watchColor,
          _bundle_plan: selectedPlan?.getAttribute('data-plan-days') ? `${selectedPlan.getAttribute('data-plan-days')}-day` : '',
          _bundle_straps: strapLabels.join(', ') || 'none'
        };
      };

      const getCartItems = ({ includeMobilePlan = false, closeout = false } = {}) => {
        const items = [];
        const bundleId = `bundle-${Date.now().toString(36)}`;
        const bundleQuantity = getBundleQuantity();
        const submitAsPlainWatchOnly = shouldSubmitAsPlainWatchOnly({ includeMobilePlan, closeout });

        const selectedVariant = getSelectedVariantOption();
        const selectedPlan = getSelectedPlanCard({ requireIntent: closeout });
        const selectedStraps = getSelectedStrapCards();
        const selectedAccessories = getSelectedAccessoryCards();
        const strapAllowanceRaw = Number(strapAllowance?.getAttribute('data-free-strap-allowance'));
        const strapFreeAllowance = Number.isFinite(strapAllowanceRaw)
          ? Math.max(0, Math.floor(strapAllowanceRaw))
          : Math.max(0, resolveStrapFreeAllowance(manifest) || 0);
        const selectedMobilePlan = includeMobilePlan ? getSelectedMobilePlanCard({ requireIntent: closeout }) : null;
        const hasUpsellSelections = hasSelectedWatchPlan({ requireIntent: closeout })
          || hasSelectedStraps()
          || hasSelectedAccessories()
          || (includeMobilePlan && Boolean(selectedMobilePlan));
        const bundleProps = hasUpsellSelections ? getBundleProperties(bundleId, { hasUpsellSelections, selectedPlan }) : {};

        const watchVariantId = Number(selectedVariant?.getAttribute('data-variant-id') || '0') || resolveBaseWatchVariantId();
        const planVariantId = Number(selectedPlan?.getAttribute('data-plan-variant-id') || '0');

        if (watchVariantId > 0) {
          items.push({
            id: watchVariantId,
            quantity: bundleQuantity,
            properties: submitAsPlainWatchOnly || !hasUpsellSelections ? {} : Object.assign({ _bundle_role: 'watch' }, bundleProps)
          });
        }

        if (planVariantId > 0 && !submitAsPlainWatchOnly) {
          items.push({
            id: planVariantId,
            quantity: 1,
            properties: Object.assign({ _bundle_role: 'plan' }, bundleProps, watchPlanDiscountProperties)
          });
        }

        if (!submitAsPlainWatchOnly) {
          selectedStraps.forEach((card, index) => {
          const strapVariantId = Number(card.getAttribute('data-product-id') || '0');
          const applyStrapDiscountProps = strapFreeAllowance > 0 ? index < strapFreeAllowance : true;
          if (strapVariantId > 0) {
            items.push({
              id: strapVariantId,
              quantity: 1,
              properties: Object.assign(
                { _bundle_role: `strap-${index + 1}` },
                bundleProps,
                applyStrapDiscountProps ? strapDiscountProperties : {}
              )
            });
          }
          });

          selectedAccessories.forEach((card, index) => {
            const accessoryVariantId = Number(card.getAttribute('data-product-id') || '0');
            const quantity = Math.max(1, Number(card.getAttribute('data-qty') || '1'));
            const isIncludedAccessory = card.hasAttribute('data-included-accessory-card');
            const accessoryLineDiscountProperties = isIncludedAccessory
              ? includedAccessoryDiscountProperties
              : accessoryDiscountProperties;

            if (accessoryVariantId > 0) {
              items.push({
                id: accessoryVariantId,
                quantity,
                properties: Object.assign(
                  { _bundle_role: isIncludedAccessory ? `accessory-included-${index + 1}` : `accessory-${index + 1}` },
                  bundleProps,
                  accessoryLineDiscountProperties
                )
              });
            }
          });
        }

        if (includeMobilePlan && mobileOfferPercentageOff !== null && !submitAsPlainWatchOnly) {
          const mobilePlanCard = selectedMobilePlan;
          const mobileVariantId = Number(mobilePlanCard?.getAttribute('data-mobile-variant-id') || '0');

          if (mobileVariantId > 0) {
            const mobileProps = Object.assign({ _bundle_role: 'mobile-plan' }, bundleProps);

            Object.assign(mobileProps, regiosProperties.mobile);

            items.push({
              id: mobileVariantId,
              quantity: 1,
              properties: mobileProps
            });

            if (getSelectedMobileSimChecked() && mobileSimVariantId > 0) {
              items.push({
                id: mobileSimVariantId,
                quantity: 1,
                properties: Object.assign({ _bundle_role: 'mobile-sim' }, bundleProps, regiosProperties.sim)
              });
            }
          }
        }

        return items;
      };

      const openCartDrawer = () => {
        window.setTimeout(() => {
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        }, 250);
      };

      const updateCartAttributes = async (attributes) => {
        const response = await fetch(theme.routes.cart_update_url, {
          ...theme.utils.fetchConfig('json'),
          body: JSON.stringify({ attributes })
        });

        if (!response.ok) {
          throw new Error('Cart update failed');
        }

        return response.json();
      };

      const addToCart = async (items) => {
        const response = await fetch(theme.routes.cart_add_url, {
          ...theme.utils.fetchConfig('json'),
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          throw new Error('Cart add failed');
        }

        return response.json();
      };

      const setButtonBusy = (button, busy, busyLabel) => {
        if (!button) {
          return;
        }

        const label = button.querySelector('.plan-drawer__btn-label');
        const textTarget = label || button;
        const originalTextKey = 'bundleDrawerOriginalText';

        if (!textTarget.dataset[originalTextKey]) {
          textTarget.dataset[originalTextKey] = textTarget.textContent || '';
        }

        button.disabled = busy;
        button.setAttribute('aria-busy', busy ? 'true' : 'false');

        if (busy && busyLabel) {
          if (label) {
            label.textContent = busyLabel;
          } else {
            button.textContent = busyLabel;
          }
        } else if (!busy) {
          const originalText = textTarget.dataset[originalTextKey];
          if (originalText) {
            textTarget.textContent = originalText;
          }
        }
      };

      const submitBundle = async ({ includeMobilePlan = false, closeout = false, button = null, busyLabel = 'Adding to cart…', errorLabel = 'Something went wrong — try again' } = {}) => {
        if (isSubmitting) {
          return;
        }

        isSubmitting = true;
        const items = getCartItems({ includeMobilePlan, closeout });
        const submitAsPlainWatchOnly = shouldSubmitAsPlainWatchOnly({ includeMobilePlan, closeout });
        if (!items.length) {
          isSubmitting = false;
          closeDrawer();
          return;
        }

        try {
          setSubmittingLayerVisible(true);
          setButtonBusy(button, true, busyLabel);

          if (!submitAsPlainWatchOnly) {
            const sessionId = sessionStorage.getItem('st_sid') || '';
            const attributes = {
              _st_session: sessionId
            };

            await updateCartAttributes(attributes);
          }

          await addToCart(items);
          closeDrawer();
          openCartDrawer();
        } catch (error) {
          console.error('[bundle-drawer]', error);
          if (button) {
            setButtonBusy(button, false);
            if (button === summaryCtaButton) {
              setSummaryCtaLabel(summaryMobileOptin ? summaryMobileOptin.classList.contains('is-checked') : false);
            } else if (button === mobilePlanCtaButton) {
              const mobilePlanCard = getSelectedMobilePlanCard();
              updateMobilePlanSelectionState(mobilePlanCard);
            } else {
              button.textContent = errorLabel;
            }
          }
        } finally {
          const elapsed = Date.now() - submittingShownAt;
          const remaining = Math.max(0, 700 - elapsed);
          window.setTimeout(() => {
            setSubmittingLayerVisible(false);
          }, remaining);
          isSubmitting = false;
        }
      };

      const updateMobilePlanSelectionState = (selectedCard) => {
        if (!mobilePlanScreen) {
          return;
        }

        if (!selectedCard) {
          if (mobilePlanCtaButton) {
            mobilePlanCtaButton.disabled = true;
          }
          return;
        }

        const planName = (selectedCard.querySelector('.plan-drawer__deal-name')?.textContent || '').trim();
        const planPrice = (selectedCard.querySelector('.plan-drawer__deal-price-now')?.textContent || '').trim();
        const planDetail = (selectedCard.querySelector('.plan-drawer__deal-meta')?.textContent || '').trim();

        if (mobilePlanName && planName) {
          mobilePlanName.textContent = planName;
        }

        if (mobilePlanDetail && planDetail) {
          mobilePlanDetail.textContent = planDetail;
        }

        if (mobilePlanCtaLabel && planName && planPrice) {
          if (getMobileOfferDiscountPercent() === 100) {
            mobilePlanCtaLabel.textContent = `Add ${planName} (First Month Free)`;
          } else {
            mobilePlanCtaLabel.textContent = `Add ${planName} (${planPrice} ${getMobileCtaDurationCopy()})`;
          }
        }

        if (mobilePlanCtaButton) {
          mobilePlanCtaButton.disabled = false;
        }
      };

      if (mobilePlanScreen) {
        updateMobilePlanPricesFromDiscount();

        mobileSkipButtons.forEach((button) => {
          button.style.display = mobilePlanAllowSkip ? '' : 'none';
          button.setAttribute('aria-hidden', mobilePlanAllowSkip ? 'false' : 'true');
          button.disabled = !mobilePlanAllowSkip;
        });

        const initiallySelectedMobilePlan = mobilePlanScreen.querySelector('.plan-drawer__deal-card.plan-drawer__plan.is-selected')
          || mobilePlanScreen.querySelector('.plan-drawer__deal-card.plan-drawer__plan');
        if (initiallySelectedMobilePlan) {
          const allMobilePlans = Array.from(mobilePlanScreen.querySelectorAll('.plan-drawer__deal-card.plan-drawer__plan'));
          allMobilePlans.forEach((planCard) => {
            planCard.classList.toggle('is-selected', planCard === initiallySelectedMobilePlan);
          });
          updateMobilePlanSelectionState(initiallySelectedMobilePlan);
        } else {
          updateMobilePlanSelectionState(null);
        }

        const initialMobileSimOptin = mobilePlanScreen.querySelector('[data-mobile-sim-optin]');
        if (initialMobileSimOptin) {
          setMobileSimOptinChecked(initialMobileSimOptin.classList.contains('is-checked'));
        }

        if (!initiallySelectedMobilePlan && mobilePlanCtaButton) {
          mobilePlanCtaButton.disabled = true;
        }
      }

      if (watchPlanSkipButton) {
        if (!showWatchPlanSkip) {
          watchPlanSkipButton.remove();
        } else {
          watchPlanSkipButton.classList.add('is-visible');
          watchPlanSkipButton.hidden = false;
          watchPlanSkipButton.setAttribute('aria-hidden', 'false');
          watchPlanSkipButton.disabled = false;
        }
      }

      const resetDrawerState = () => {
        syncInitialVariantSelectionFromPdp();
        syncBundleQuantity(resolveSourceQuantity() ?? quantityInput?.value ?? 1);
        userSelectionIntent.watchPlan = false;
        userSelectionIntent.mobilePlan = false;
        userSelectionIntent.mobileSim = false;
        watchPlanSkipped = false;
        freeStrapSelectionOrder = 0;

        // Clear any stale submitting state from the previous successful ATC.
        overlay.querySelectorAll('[aria-busy="true"]').forEach((button) => {
          setButtonBusy(button, false);
        });

        const variantOptions = Array.from(overlay.querySelectorAll('[data-screen-type="variant_selector"] [data-variant-option]'));
        if (variantOptions.length) {
          const fallbackIndex = Math.max(0, Math.min(resolveVariantOptionIndex(variantOptions), variantOptions.length - 1));
          const selectedOption = variantOptions[fallbackIndex];
          const hasAvailableOptions = variantOptions.some((option) => isVariantOptionAvailable(option));
          variantOptions.forEach((option, index) => {
            const available = isVariantOptionAvailable(option);
            option.classList.toggle('is-selected', available && index === fallbackIndex);
            option.disabled = !available;
            option.setAttribute('aria-disabled', available ? 'false' : 'true');
            option.tabIndex = available ? 0 : -1;
          });

          const variantScreen = overlay.querySelector('[data-screen-type="variant_selector"]');
          const selectedColour = variantScreen?.querySelector('[data-variant-selected-colour]');
          const ctaLabel = variantScreen?.querySelector('[data-variant-cta-label]');
          const ctaButton = variantScreen?.querySelector('.pud-partner__cta');
          const colour = selectedOption?.getAttribute('data-colour') || '';

          if (selectedColour) {
            selectedColour.textContent = colour;
          }

          if (ctaButton) {
            ctaButton.disabled = !hasAvailableOptions;
            ctaButton.classList.toggle('is-sold-out', !hasAvailableOptions);
          }

          if (ctaLabel) {
            ctaLabel.textContent = hasAvailableOptions && colour ? `Select ${colour}` : 'Out of stock';
          }

          if (ctaLabel && colour && hasAvailableOptions) {
            ctaLabel.textContent = `Select ${colour}`;
          }
        }

        const watchPlanOptions = Array.from(overlay.querySelectorAll('[data-watch-plan-screen] [data-watch-plan-option]'));
        if (watchPlanOptions.length) {
          const fallbackIndex = Math.max(0, Math.min(initialSelectionState.watchPlanIndex, watchPlanOptions.length - 1));
          const selectedPlan = watchPlanOptions[fallbackIndex];
          watchPlanOptions.forEach((option, index) => {
            option.classList.toggle('is-selected', index === fallbackIndex);
          });

          const ctaLabel = overlay.querySelector('[data-watch-plan-screen] [data-watch-plan-cta-label]');
          const planName = selectedPlan?.getAttribute('data-plan-name') || 'Watch Plan';
          const planPrice = selectedPlan?.getAttribute('data-plan-price') || '';
          if (ctaLabel) {
            ctaLabel.textContent = `Select ${planName}${planPrice ? ` (${planPrice})` : ''}`;
          }
        }

        const strapCards = Array.from(overlay.querySelectorAll('[data-screen-type="strap_selector"] [data-free-strap-card]'));
        strapCards.forEach((card) => {
          const shouldSelect = initialSelectionState.strapSelectedCards.has(card);
          card.classList.toggle('is-selected', shouldSelect);
          if (shouldSelect) {
            freeStrapSelectionOrder += 1;
            card.setAttribute('data-free-strap-rank', String(freeStrapSelectionOrder));
          } else {
            card.removeAttribute('data-free-strap-rank');
          }
        });

        initialSelectionState.accessoryQtyByCard.forEach((qty, card) => {
          const sanitizedQty = Number.isFinite(qty) && qty > 0 ? Math.floor(qty) : 0;
          card.setAttribute('data-qty', String(sanitizedQty));
        });

        const mobilePlanOptions = Array.from(overlay.querySelectorAll('[data-screen-type="mobile_plan"] [data-mobile-plan-option]'));
        if (mobilePlanOptions.length) {
          const fallbackIndex = Math.max(0, Math.min(initialSelectionState.mobilePlanIndex, mobilePlanOptions.length - 1));
          const selectedMobile = mobilePlanOptions[fallbackIndex];
          mobilePlanOptions.forEach((option, index) => {
            option.classList.toggle('is-selected', index === fallbackIndex);
          });
          updateMobilePlanSelectionState(selectedMobile);
        }

        setMobileSimOptinChecked(initialSelectionState.mobileSimChecked);
        setSummaryMobileOptinChecked(initialSelectionState.summaryMobileOptinChecked);
        updateFreeStrapState();
        updateAccessoryState();
        updateOrderSummary();
      };

      updateFreeStrapState();
      updateAccessoryState();
      updateOrderSummary();

      const openDrawer = () => {
        resetDrawerState();
        overlay.hidden = false;
        document.body.classList.add('bundle-drawer-is-open');
        document.body.style.overflow = 'hidden';
        trigger.setAttribute('aria-expanded', 'true');
        flow.reset();
        updateAccessoryState();
      };

      const closeDrawer = () => {
        overlay.hidden = true;
        document.body.classList.remove('bundle-drawer-is-open');
        document.body.style.overflow = '';
        trigger.setAttribute('aria-expanded', 'false');
      };

      trigger.addEventListener('click', openDrawer);

      const syncEntryTriggerFromVariantChange = (event) => {
        const eventVariant = event?.detail?.variant && typeof event.detail.variant === 'object'
          ? event.detail.variant
          : null;

        if (eventVariant) {
          const sourceProductInfo = event.target?.closest?.('product-info');
          if (sourceProductInfo && productInfo && sourceProductInfo !== productInfo) {
            return;
          }
          if (!Array.isArray(bundleProduct.variants) || !bundleProduct.variants.some((variant) => Number(variant.id) === Number(eventVariant.id))) {
            return;
          }
          latestPdpVariant = eventVariant;
        }

        updateEntryTriggerState(eventVariant);
        updateSummaryCtaState();
        if (!flow.hasType('variant_selector')) {
          updateOrderSummary();
        }
      };

      const activeProductForm = getActiveProductForm();
      if (activeProductForm) {
        activeProductForm.addEventListener('variant:change', syncEntryTriggerFromVariantChange);
      }

      document.addEventListener('variant:change', syncEntryTriggerFromVariantChange);
      document.addEventListener('change', (event) => {
        const target = event.target;
        if (!target || !productFormId) {
          return;
        }

        const form = target.closest(`form#${CSS.escape(productFormId)}`) || target.closest(`[data-product-form][id="${productFormId}"]`) || target.closest(`[form="${productFormId}"]`);
        if (form) {
          updateEntryTriggerState();
        }
      });

      overlay.addEventListener('click', (event) => {
        const summaryMobileToggle = event.target.closest('[data-mobile-optin]');
        if (summaryMobileToggle && shouldShowMobilePlanInSummary) {
          const nextChecked = !summaryMobileToggle.classList.contains('is-checked');
          setSummaryMobileOptinChecked(nextChecked);
          return;
        }

        const accessoryQtyAdd = event.target.closest('[data-accessory-qty-add]');
        if (accessoryQtyAdd) {
          const card = accessoryQtyAdd.closest('[data-accessory-addon-card]');

          if (card) {
            const currentQty = Number(card.getAttribute('data-qty') || '0');
            const nextQty = Number.isFinite(currentQty) ? Math.min(99, Math.max(0, Math.floor(currentQty) + 1)) : 1;
            card.setAttribute('data-qty', String(nextQty));
            updateAccessoryState();
          }

          return;
        }

        const accessoryQtyDec = event.target.closest('[data-accessory-qty-dec]');
        if (accessoryQtyDec) {
          const card = accessoryQtyDec.closest('[data-accessory-addon-card]');

          if (card) {
            const currentQty = Number(card.getAttribute('data-qty') || '0');
            const nextQty = Number.isFinite(currentQty) ? Math.max(0, Math.floor(currentQty) - 1) : 0;
            card.setAttribute('data-qty', String(nextQty));
            updateAccessoryState();
          }

          return;
        }

        const accessoryQtyInc = event.target.closest('[data-accessory-qty-inc]');
        if (accessoryQtyInc) {
          const card = accessoryQtyInc.closest('[data-accessory-addon-card]');

          if (card) {
            const currentQty = Number(card.getAttribute('data-qty') || '0');
            const nextQty = Number.isFinite(currentQty) ? Math.min(99, Math.floor(currentQty) + 1) : 1;
            card.setAttribute('data-qty', String(nextQty));
            updateAccessoryState();
          }

          return;
        }

        const accessoryCard = event.target.closest('[data-accessory-addon-card]');
        if (accessoryCard) {
          const insideControls = event.target.closest('[data-accessory-qty-add], [data-accessory-qty-dec], [data-accessory-qty-inc]');

          if (!insideControls) {
            const currentQty = Number(accessoryCard.getAttribute('data-qty') || '0');
            const nextQty = Number.isFinite(currentQty) && currentQty > 0 ? 0 : 1;
            accessoryCard.setAttribute('data-qty', String(nextQty));
            updateAccessoryState();
          }

          return;
        }

        const freeStrapCard = event.target.closest('[data-free-strap-card]');

        if (freeStrapCard) {
          const freeStrapScreen = freeStrapCard.closest('[data-screen-type="strap_selector"]');

          if (freeStrapScreen) {
            if (freeStrapCard.classList.contains('is-selected')) {
              freeStrapCard.classList.remove('is-selected');
              freeStrapCard.removeAttribute('data-free-strap-rank');
            } else {
              freeStrapSelectionOrder += 1;
              freeStrapCard.classList.add('is-selected');
              freeStrapCard.setAttribute('data-free-strap-rank', String(freeStrapSelectionOrder));
            }

            updateFreeStrapState();
            updateOrderSummary();
          }

          return;
        }

        const watchPlanOption = event.target.closest('[data-watch-plan-option]');

        if (watchPlanOption) {
          const watchPlanScreen = watchPlanOption.closest('[data-watch-plan-screen]');

          if (watchPlanScreen) {
            watchPlanScreen.querySelectorAll('[data-watch-plan-option]').forEach((option) => {
              option.classList.toggle('is-selected', option === watchPlanOption);
            });
            watchPlanSkipped = false;
            userSelectionIntent.watchPlan = true;

            const ctaLabel = watchPlanScreen.querySelector('[data-watch-plan-cta-label]');
            const planName = watchPlanOption.getAttribute('data-plan-name') || 'Watch Plan';
            const planPrice = watchPlanOption.getAttribute('data-plan-price') || '';

            if (ctaLabel) {
              ctaLabel.textContent = `Select ${planName} (${planPrice})`;
            }

            updateOrderSummary();
          }

          return;
        }

        const mobileSimOptin = event.target.closest('[data-mobile-sim-optin]');
        if (mobileSimOptin) {
          const nextChecked = !mobileSimOptin.classList.contains('is-checked');
          setMobileSimOptinChecked(nextChecked);
          userSelectionIntent.mobileSim = true;
          return;
        }

        const mobilePlanOption = event.target.closest('.plan-drawer__deal-card.plan-drawer__plan');
        if (mobilePlanOption && mobilePlanOption.closest('[data-screen-type="mobile_plan"]')) {
          const mobilePlans = mobilePlanScreen
            ? Array.from(mobilePlanScreen.querySelectorAll('.plan-drawer__deal-card.plan-drawer__plan'))
            : [];

          mobilePlans.forEach((option) => {
            option.classList.toggle('is-selected', option === mobilePlanOption);
          });

          userSelectionIntent.mobilePlan = true;
          updateMobilePlanSelectionState(mobilePlanOption);
          return;
        }

        const variantOption = event.target.closest('[data-variant-option]');

        if (variantOption) {
          if (!isVariantOptionAvailable(variantOption) || variantOption.disabled) {
            return;
          }

          const variantScreen = variantOption.closest('[data-screen-type="variant_selector"]');

          if (variantScreen) {
            variantScreen.querySelectorAll('[data-variant-option]').forEach((option) => {
              option.classList.toggle('is-selected', option === variantOption);
            });

            const selectedColour = variantScreen.querySelector('[data-variant-selected-colour]');
            const ctaLabel = variantScreen.querySelector('[data-variant-cta-label]');
            const ctaButton = variantScreen.querySelector('.pud-partner__cta');
            const colour = variantOption.getAttribute('data-colour') || '';

            if (selectedColour) {
              selectedColour.textContent = colour;
            }

            if (ctaButton) {
              ctaButton.disabled = false;
              ctaButton.classList.remove('is-sold-out');
            }

            if (ctaLabel) {
              ctaLabel.textContent = `Select ${colour}`;
            }

            updateOrderSummary();
          }

          return;
        }

        if (event.target.closest(closeSelector)) {
          if (shouldAutoSubmitOnClose) {
            submitBundle({
              includeMobilePlan: shouldIncludeMobileOnClose({ requireIntent: true }),
              closeout: true,
              button: null,
              busyLabel: 'Adding to cart…'
            });
            return;
          }

          closeDrawer();
          return;
        }

        if (event.target.closest(backSelector)) {
          flow.prev();
          updateAccessoryState();
          return;
        }

        const editButton = event.target.closest(editSelector);

        if (editButton) {
          event.preventDefault();
          flow.goTo(targetIdToType(editButton.getAttribute('data-summary-target')));
          updateAccessoryState();
          return;
        }

        if (event.target.closest(nextSelector)) {
          const nextButton = event.target.closest(nextSelector);
          const inSummaryScreen = nextButton?.closest('[data-screen-type="order_summary"]');
          const activeScreenType = flow.activeType();
          const nextScreenType = flow.nextType();

          if (nextButton === mobilePlanCtaButton || nextButton === mobileSkipButton) {
            return;
          }

          if (nextButton === watchPlanSkipButton) {
            event.preventDefault();
            if (watchPlanRequired) {
              return;
            }
            watchPlanSkipped = true;
            userSelectionIntent.watchPlan = false;
            if (flow.isLast()) {
              submitBundle({ includeMobilePlan: false, button: nextButton, busyLabel: 'Adding to cart…' });
              return;
            }

            flow.next();
            updateAccessoryState();
            return;
          }

          if (
            mobilePlanRequired
            && hasMobilePlanUpsell
            && activeScreenType !== 'mobile_plan'
            && nextScreenType === 'order_summary'
            && !getSelectedMobilePlanCard({ requireIntent: false })
          ) {
            event.preventDefault();
            flow.goTo('mobile_plan');
            updateAccessoryState();
            return;
          }

          // Treat progressing past the watch plan screen as explicit intent to keep
          // the currently selected plan, even when it is the preselected default.
          if (activeScreenType === 'watch_plan' && getSelectedPlanCard({ requireIntent: false })) {
            userSelectionIntent.watchPlan = true;
          }

          // Do the same for mobile plan when advancing through that screen.
          if (activeScreenType === 'mobile_plan' && getSelectedMobilePlanCard({ requireIntent: false })) {
            userSelectionIntent.mobilePlan = true;
          }

          if (inSummaryScreen && shouldShowMobilePlanInSummary && summaryMobileOptin) {
            const wantsMobileUpsell = summaryMobileOptin.classList.contains('is-checked');
            const selectedMobilePlan = getSelectedMobilePlanCard({ requireIntent: false });

            if (mobilePlanRequired) {
              event.preventDefault();
              if (!selectedMobilePlan) {
                flow.goTo('mobile_plan');
                updateAccessoryState();
                return;
              }

              submitBundle({ includeMobilePlan: true, button: nextButton, busyLabel: 'Adding to cart…' });
              return;
            }

            if (wantsMobileUpsell) {
              if (nextScreenType === 'mobile_plan') {
                event.preventDefault();
                flow.next();
                updateAccessoryState();
                return;
              }

              if (selectedMobilePlan) {
                event.preventDefault();
                submitBundle({ includeMobilePlan: true, button: nextButton, busyLabel: 'Adding to cart…' });
                return;
              }

              event.preventDefault();
              flow.goTo('mobile_plan');
              updateAccessoryState();
              return;
            }

            event.preventDefault();
            submitBundle({ includeMobilePlan: false, button: nextButton, busyLabel: 'Adding to cart…' });
            return;
          }

          if (flow.isLast()) {
            event.preventDefault();
            submitBundle({ includeMobilePlan: false, button: nextButton, busyLabel: 'Adding to cart…' });
            return;
          }

          flow.next();
          updateAccessoryState();
        }
      });

      if (mobilePlanCtaButton) {
        mobilePlanCtaButton.addEventListener('click', () => {
          const selectedMobilePlan = getSelectedMobilePlanCard({ requireIntent: false });
          if (mobilePlanRequired && !selectedMobilePlan) {
            return;
          }

          if (flow.activeType() === 'mobile_plan' && !flow.isLast()) {
            userSelectionIntent.mobilePlan = Boolean(selectedMobilePlan);
            flow.next();
            updateAccessoryState();
            return;
          }

          submitBundle({ includeMobilePlan: true, button: mobilePlanCtaButton, busyLabel: 'Adding to cart…' });
        });
      }

      if (mobileSkipButton && mobilePlanAllowSkip) {
        mobileSkipButton.addEventListener('click', () => {
          submitBundle({ includeMobilePlan: false, button: mobileSkipButton, busyLabel: 'Adding…' });
        });
      }

      if (summaryMobileOptin) {
        summaryMobileOptin.addEventListener('keydown', (event) => {
          if (!shouldShowMobilePlanInSummary) {
            return;
          }

          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const nextChecked = !summaryMobileOptin.classList.contains('is-checked');
            setSummaryMobileOptinChecked(nextChecked);
          }
        });
      }

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !overlay.hidden) {
          if (shouldAutoSubmitOnClose) {
            submitBundle({ includeMobilePlan: shouldIncludeMobileOnClose({ requireIntent: true }), closeout: true, button: null, busyLabel: 'Adding to cart…' });
            return;
          }

          closeDrawer();
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();